// ============================================================================
//  MarkerMaker.js
//  ---------------------------------------------------------------------------
//  Full Marker-Making pipeline (as per Nirvikalp CAD Plan, 27 May 2026)
//
//  STEP 1  → StyleSelector          (same filters as Workstation)
//  STEP 2  → Pattern Loader          (load all patterns saved with style)
//  STEP 3  → Spec Sheet Import        (CSV / JSON / paste from Style Registry)
//  STEP 4  → Spec→Pattern Cross-Check (measurement comparison & rule-based adj.)
//  STEP 5  → Pattern Editor           (notches, grain lines, darts, measure tools)
//  STEP 6  → Grading View             (size nesting in layers, compatibility check)
//  STEP 7  → Seam Compatibility       (neck vs collar, cuff vs sleeve, etc.)
//  STEP 8  → Marker Planner           (3 modes: PO ratio | manual | auto)
//
//  Usage:
//    import MarkerMaker from './MarkerMaker';
//    <MarkerMaker db={db} onClose={() => {}} />
//
//  The component expects `db.masterStyles[]` and `db.masterParts[]` which
//  already exist in the Nirvikalp state tree populated by Workstation /
//  PartDNA. All API calls use the same save_universal.php endpoint.
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import GARMENT_STANDARDS, { getGradingTable, buildStandardPatterns, getGradedMeasurements, TEMPLATE_ANCHORS, TEMPLATE_MEASUREMENTS, calcMeasurementsFromAnchors } from './GarmentStandards';
import { GARMENT_TEMPLATES } from './ShapeBuilder';  // reuse ShapeBuilder's parametric shapes in MarkerMaker
import {
  ChevronRight, ChevronLeft, X, Check, Upload, Download,
  Ruler, Scissors, Grid, Layers, AlertTriangle, CheckCircle2,
  Plus, Trash2, Edit2, Eye, ZoomIn, ZoomOut, RotateCcw,
  RefreshCw, Package, FileText, Settings, Search, Save
} from 'lucide-react';

// ---------------------------------------------------------------------------
//  CONSTANTS & API
// ---------------------------------------------------------------------------
const API_BASE = 'https://nirvikalp.integrashion.com/api';
const SAVE_URL  = `${API_BASE}/save_universal.php`;
const LOAD_URL  = `${API_BASE}/load_universal.php`;

const GARMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

// Default seam rules: which measurements must match across seams
const DEFAULT_SEAM_RULES = [
  { id: 'neck_collar',   label: 'Neckline ↔ Collar Band',       partA: 'neck',         partB: 'collar_band',   specA: 'neckWidth',   specB: 'neckLength',   tolerance: 0.5 },
  { id: 'armhole_sleeve',label: 'Armhole ↔ Sleeve Crown',        partA: 'body',         partB: 'sleeve',        specA: 'armholeDrop', specB: 'capHeight',    tolerance: 0.8 },
  { id: 'cuff_sleeve',   label: 'Cuff Length ↔ Sleeve Opening',  partA: 'cuff',         partB: 'sleeve',        specA: 'cuffLength',  specB: 'cuffWidth',    tolerance: 0.3 },
  { id: 'side_seam',     label: 'Front Side Seam ↔ Back Side',   partA: 'tee_front',    partB: 'tee_back',      specA: 'bodyLength',  specB: 'bodyLength',   tolerance: 0.0 },
  { id: 'yoke_back',     label: 'Yoke Width ↔ Back Shoulder',    partA: 'yoke',         partB: 'tee_back',      specA: 'yokeWidth',   specB: 'shoulderWidth',tolerance: 0.5 },
];

// ============================================================================
//  PATTERN FILE FORMAT  (.npf — Nirvikalp Pattern File, JSON)
//  Lightweight, portable; can be used for texture fill or 3D simulation.
// ============================================================================
export const createEmptyPattern = (partId, partName, template = '', measurements = {}) => ({
  format: 'npf-v1',
  id: `pat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  partId,
  partName,
  template,
  measurements,                    // finished-garment measurements (cm)
  seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },  // default SA in cm
  grainLine: { x1: 0, y1: 0, x2: 0, y2: 1, angleDeg: 90 },  // warp = vertical
  notches: [],     // [{ id, t (0-1 edge position), edgeIdx, type:'slit'|'v', depth:1 }]
  darts: [],       // [{ id, x, y, width, depth, angle }]
  measurementPoints: [],  // [{ id, label, x1, y1, x2, y2 }]
  gradingRules: {},       // { S: { dx: {}, dy: {} }, M: {}, ... }  delta per measurement key
  specMapping: {},        // { specSheetKey: measurementKey }
  brandRules: [],         // [{ field, operator, value, action }]
  createdOn: new Date().toISOString(),
  updatedOn: new Date().toISOString(),
});

// Serialise to downloadable JSON file
export const exportPatternFile = (pattern) => {
  const blob = new Blob([JSON.stringify(pattern, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${pattern.partName || 'pattern'}_${pattern.id}.npf`;
  a.click();
};

// Parse spec-sheet text (CSV / JSON / paste)
// ── Garment industry fraction parser: "17 3/4"→17.75, "1 1/8"→1.125 ─────────
export const parseFrac = (s) => {
  const str = String(s || '').trim().replace(/['"]/g, '');
  if (!str || str === '-' || /^n\/?a$/i.test(str)) return NaN;
  const m1 = str.match(/^([\d.]+)\s+(\d+)\/(\d+)$/);
  if (m1) return parseFloat(m1[1]) + parseFloat(m1[2]) / parseFloat(m1[3]);
  const m2 = str.match(/^(\d+)\/(\d+)$/);
  if (m2) return parseFloat(m2[1]) / parseFloat(m2[2]);
  return parseFloat(str);
};

// ── Parse one CSV line, respecting quoted commas ──────────────────────────
const parseCsvRow = (line) => {
  const r = []; let q = false, cur = '';
  for (const ch of line) {
    if (ch === '"')     { q = !q; }
    else if (ch === ',' && !q) { r.push(cur.trim()); cur = ''; }
    else { cur += ch; }
  }
  r.push(cur.trim());
  return r;
};

// ── Full garment spec sheet parser ────────────────────────────────────────
// Handles the real industry CSV format:
//   Row 1-4:  metadata  (BRAND, FIT, GENDER, VENDOR …)
//   Row N:    header    POINT OF MEASUREMENT | TOL +/- | S | (blank) | M | (blank) | L …
//   Row N+x:  measurements, with values that may be fractions ("17 3/4")
// Also handles simple key:value text and JSON as fallbacks.
export const parseSpecSheet = (raw) => {
  if (!raw) return {};
  const txt = raw.trim().replace(/^\uFEFF/, ''); // strip BOM

  // JSON fallback
  if (txt.startsWith('{') || txt.startsWith('[')) {
    try {
      const j = JSON.parse(txt);
      if (Array.isArray(j)) {
        const out = {};
        j.forEach(r => {
          const k = r.key || r.measurement || r.name || r.description;
          const v = r.value ?? r.val ?? r.current ?? r.target;
          if (k) out[String(k).trim()] = parseFloat(v);
        });
        return { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'json' };
      }
      const out = {};
      Object.entries(j).forEach(([k, v]) => { out[k.trim()] = parseFloat(v); });
      return { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'json' };
    } catch (_) {}
  }

  // CSV multi-column format
  const rows = txt.split(/\r?\n/).map(parseCsvRow);
  const META_KEYS = ['BRAND','FIT','GENDER','VENDOR','SEASON','STYLE','DATE','CUSTOMER'];
  const meta = {};
  const sizeColMap = {}; // { 'S': 2, 'M': 4, … }
  let headerIdx = -1;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const c0 = (rows[i][0] || '').trim();
    const c0u = c0.toUpperCase();
    if (META_KEYS.some(k => c0u.startsWith(k))) {
      meta[c0] = (rows[i][1] || '').trim();
    }
    if (c0u.includes('POINT') || c0u.includes('MEASUREMENT') || c0u === 'POM') {
      headerIdx = i;
      rows[i].forEach((cell, ci) => {
        const sz = cell.trim().toUpperCase();
        if (/^(XXS|XS|S|M|L|XL|2XL|XXL|3XL|XXXL|\d{2}|\d{2}\/\d{2})$/.test(sz))
          sizeColMap[sz] = ci;
      });
      break;
    }
  }

  const sizes = Object.keys(sizeColMap);

  if (headerIdx !== -1 && sizes.length > 0) {
    const base = {}, sizeData = {}, tols = {};
    sizes.forEach(sz => (sizeData[sz] = {}));

    for (let i = headerIdx + 1; i < rows.length; i++) {
      const row = rows[i];
      const name = (row[0] || '').replace(/"/g, '').trim();
      if (!name || /^(SIGN|DATE|PAGE)/i.test(name)) continue;
      if (!sizes.some(sz => !isNaN(parseFrac(row[sizeColMap[sz]])))) continue;

      const tol = parseFrac(row[1]);
      if (!isNaN(tol)) tols[name] = tol;
      sizes.forEach(sz => {
        const v = parseFrac(row[sizeColMap[sz]]);
        if (!isNaN(v)) sizeData[sz][name] = v;
      });
    }

    const baseSz = sizeColMap['M'] ? 'M' : sizes[Math.floor(sizes.length / 2)];
    Object.assign(base, sizeData[baseSz] || {});

    // Detect inches: body-type measurements < 50 are almost certainly in inches
    const sample = Object.values(base)[0] || 100;
    const isInches = sample > 0 && sample < 50;

    return { base, sizeData, sizes, tolerances: tols, meta, baseSize: baseSz, isInches, _format: 'garment_csv' };
  }

  // Simple key:value text fallback
  const out = {};
  txt.split(/\r?\n/).forEach(line => {
    const parts = line.split(/[,\t:;=|]/).map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const v = parseFrac(parts[parts.length - 1]);
      if (!isNaN(v)) out[parts[0]] = v;
    }
  });
  return Object.keys(out).length
    ? { base: out, sizeData: {}, sizes: [], meta: {}, isInches: false, _format: 'simple' }
    : {};
};

// Fuzzy match spec-sheet keys to pattern measurement keys
const normKey = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');
export const fuzzyMapSpec = (specData, measurementKeys) => {
  const mapping = {};
  measurementKeys.forEach(mk => {
    const nk = normKey(mk);
    const hit = Object.keys(specData).find(sk => {
      const ns = normKey(sk);
      return ns === nk || ns.includes(nk) || nk.includes(ns);
    });
    if (hit) mapping[hit] = mk;
  });
  return mapping;
};

// Apply grading delta to base measurements to get measurements for a size
const applyGrading = (baseM, gradingRules, size) => {
  const rule = gradingRules[size];
  if (!rule) return { ...baseM };
  const out = { ...baseM };
  Object.entries(rule).forEach(([key, delta]) => {
    if (out[key] !== undefined) out[key] = +(out[key] + delta).toFixed(2);
  });
  return out;
};

// ============================================================================
//  SVG PATTERN RENDERER
//  Converts template measurements + notches/grain/marks into an SVG preview.
//  If no template, renders a simple bounding box.
// ============================================================================
const PatternSvg = ({
  pattern,
  width = 320,
  height = 320,
  showNotches = true,
  showGrain = true,
  showMeasurements = true,
  showSA = false,
  highlightColor = '#3b82f6',
  fillColor = 'rgba(59,130,246,0.07)',
  selected = false,
  onClick,
}) => {
  // Use the exact same shape source as the Reshape editor — tracedPoints,
  // then a saved "Standard" shape, then the anatomical SHAPE_FNS/ShapeBuilder
  // generators, then a generic box as last resort. Previously this component
  // guessed a generic shirt-body outline from width/height regardless of
  // piece type, which is why trouser panels/waistbands/pockets all rendered
  // as near-identical warped blobs in the library cards.
  const { pts, ptsMeta } = useMemo(() => buildDisplayPoly(pattern),
    [JSON.stringify(pattern.tracedPoints), JSON.stringify(pattern.measurements), pattern.template]);

  const xs = pts.map(p => p[0]);
  const ys = pts.map(p => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const pw = maxX - minX || 1, ph = maxY - minY || 1;
  const PAD = 12;
  const scaleX = (width - PAD * 2) / pw;
  const scaleY = (height - PAD * 2) / ph;
  const sc = Math.min(scaleX, scaleY);

  const tx = (x) => PAD + (x - minX) * sc;
  const ty = (y) => height - PAD - (y - minY) * sc;

  const outlineD = buildPathD(pts, ptsMeta, tx, ty);

  // SA offset (simple bbox expand)
  const saPolyStr = showSA ? (() => {
    const sa = Math.max(Object.values(pattern.seamAllowances || {}));
    const saP = sa * sc;
    const saPts = [
      [minX - sa, minY - sa], [maxX + sa, minY - sa],
      [maxX + sa, maxY + sa], [minX - sa, maxY + sa],
    ];
    return saPts.map(p => `${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ');
  })() : null;

  // Grain line
  const grainMidX = (tx(minX) + tx(maxX)) / 2;
  const grainY1 = ty(minY + (maxY - minY) * 0.2);
  const grainY2 = ty(minY + (maxY - minY) * 0.8);

  // Measurement annotation lines
  const measLines = showMeasurements ? (pattern.measurementPoints || []) : [];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ cursor: onClick ? 'pointer' : 'default', border: selected ? `2px solid ${highlightColor}` : '1px solid #e2e8f0', borderRadius: 6, background: '#fafafa' }}
      onClick={onClick}
    >
      {saPolyStr && (
        <polygon points={saPolyStr} fill="none" stroke="#dc2626" strokeWidth="0.8" strokeDasharray="4,3" />
      )}
      <path
        d={outlineD}
        fill={fillColor}
        stroke={selected ? highlightColor : '#334155'}
        strokeWidth={selected ? 1.5 : 1}
        strokeLinejoin="round"
      />
      {showGrain && (
        <g>
          <line x1={grainMidX} y1={grainY1} x2={grainMidX} y2={grainY2}
            stroke="#059669" strokeWidth="1.2" strokeDasharray="3,2" />
          <polygon
            points={`${grainMidX - 4},${grainY1 + 8} ${grainMidX + 4},${grainY1 + 8} ${grainMidX},${grainY1}`}
            fill="#059669"
          />
          <polygon
            points={`${grainMidX - 4},${grainY2 - 8} ${grainMidX + 4},${grainY2 - 8} ${grainMidX},${grainY2}`}
            fill="#059669"
          />
        </g>
      )}
      {showNotches && (pattern.notches || []).map((n, i) => {
        const edgeIdx = n.edgeIdx ?? 0;
        const [ax, ay] = pts[edgeIdx % pts.length];
        const [bx, by] = pts[(edgeIdx + 1) % pts.length];
        const t = n.t ?? 0.5;
        const nx = tx(ax + (bx - ax) * t);
        const ny_ = ty(ay + (by - ay) * t);
        return (
          <g key={n.id || i}>
            <line x1={nx - 5} y1={ny_} x2={nx + 5} y2={ny_} stroke="#f59e0b" strokeWidth="1.5" />
            <line x1={nx} y1={ny_ - 6} x2={nx} y2={ny_ + 6} stroke="#f59e0b" strokeWidth="1.5" />
          </g>
        );
      })}
      <text x={width / 2} y={height - 4} textAnchor="middle" fontSize="9" fill="#64748b">{pattern.partName}</text>
    </svg>
  );
};


// ============================================================================
//  PRODUCT LIBRARY — Shirts, Trousers, T-Shirts, Polo T-Shirts
//  Industry-standard pattern shapes and grading rules (ISO / AATCC basis)
//  Base size: M (shirts/tshirts/polo) · 32 (trousers)
//  Shapes stored as polygon points in cm, warp-aligned (y = grain direction)
// ============================================================================

// Helper: generate polygon points for standard part types
// All shapes parameterised by measurements in cm.
// Points are [x, y] pairs starting at bottom-left, going clockwise.
// x = width direction (WEFT),  y = length direction (WARP = grain line)

const SHAPES = {

  // ── FRONT/BACK BODY (shared logic, direction flag for neckline depth) ──────
  body: ({ halfChest, halfWaist, halfHip, bodyLen, halfShoulder, neckW, armDrop, frontNeck = 2.5, isFront = true }) => {
    const shoulderSlope = isFront ? 2.0 : 1.5;
    const neckDrop      = isFront ? frontNeck : 1.0;
    const waistInset    = halfChest - halfWaist;
    const hipInset      = halfChest - halfHip;
    return [
      [0,                  0],                              // CF/CB hem
      [halfChest,          0],                              // side seam hem
      [halfChest - hipInset*0.3,  bodyLen * 0.28],          // hip flare point
      [halfChest - waistInset*0.5, bodyLen * 0.45],          // waist suppression
      [halfChest,          bodyLen - armDrop],              // side seam at underarm
      [halfShoulder + 1.2, bodyLen - armDrop * 0.35],       // armhole curve
      [halfShoulder,       bodyLen - shoulderSlope],        // shoulder tip
      [neckW,              bodyLen],                        // neckline–shoulder
      [0,                  bodyLen - neckDrop],             // CF/CB neckline
    ];
  },

  // ── SLEEVE ──────────────────────────────────────────────────────────────────
  sleeve: ({ cuffW, bicepW, crownH, length, isShort = false }) => {
    const cap  = isShort ? crownH * 0.85 : crownH;
    const taper = (bicepW - cuffW) / 2;
    return [
      [0,               0],                       // left cuff end
      [cuffW,           0],                       // right cuff end
      [cuffW + taper,   length - cap],            // right underarm
      [cuffW + taper + cap * 0.18, length - cap * 0.4], // right cap shoulder
      [cuffW / 2 + taper * 0.5, length],          // crown peak
      [-cap * 0.08,     length - cap * 0.4],      // left cap shoulder
      [-taper * 0.1,    length - cap],            // left underarm
    ];
  },

  // ── COLLAR FALL (curved / spread collar) ─────────────────────────────────
  collarFall: ({ halfNeck, fallH, spread = 0.8 }) => [
    [0,           0],
    [halfNeck,    0],
    [halfNeck + spread, fallH * 0.5],
    [halfNeck + spread * 0.5, fallH],
    [0,           fallH],
  ],

  // ── COLLAR BAND / NECK BAND ───────────────────────────────────────────────
  band: ({ halfNeck, bandH }) => [
    [0, 0], [halfNeck + 1.5, 0], [halfNeck + 1.5, bandH], [0, bandH],
  ],

  // ── CUFF (shirt) ─────────────────────────────────────────────────────────
  cuff: ({ cuffW, cuffH }) => [
    [0, 0], [cuffW, 0], [cuffW, cuffH], [0, cuffH],
  ],

  // ── YOKE (shirt back) ───────────────────────────────────────────────────
  yoke: ({ halfShoulder, yokeDepth, neckW }) => [
    [0,            0],
    [halfShoulder, 0],
    [halfShoulder, yokeDepth - 1.5],
    [neckW,        yokeDepth],
    [0,            yokeDepth],
  ],

  // ── CHEST POCKET ─────────────────────────────────────────────────────────
  pocket: ({ pW, pH }) => [
    [0, 0], [pW, 0], [pW, pH], [0, pH],
  ],

  // ── WAISTBAND (trouser) ──────────────────────────────────────────────────
  waistband: ({ halfWaist, bandH }) => [
    [0, 0], [halfWaist + 3, 0], [halfWaist + 3, bandH], [0, bandH],
  ],

  // ── TROUSER FRONT PANEL ──────────────────────────────────────────────────
  trouserFront: ({ waistHalf, hipHalf, thighHalf, kneeHalf, hemHalf, rise, outseam }) => {
    const inseam = outseam - rise;
    return [
      [hemHalf,   0],                    // hem inner
      [hemHalf,   0],                    // same (fold line placeholder)
      [kneeHalf,  outseam * 0.4],        // knee
      [thighHalf, outseam - rise * 0.8], // thigh
      [hipHalf,   outseam - rise * 0.3], // hip
      [waistHalf, outseam],              // waist side
      [waistHalf * 0.45, outseam + 0.5], // waist CF (with slight tuck)
      [0,         outseam - rise * 0.05],// fork point
      [hemHalf * 0.5, 0],                // hem inner seam
    ];
  },

  // ── TROUSER BACK PANEL ───────────────────────────────────────────────────
  trouserBack: ({ waistHalf, hipHalf, thighHalf, kneeHalf, hemHalf, rise, outseam, seatLift = 3 }) => [
    [hemHalf,   0],
    [kneeHalf * 1.05, outseam * 0.4],
    [thighHalf * 1.08, outseam - rise * 0.75],
    [hipHalf * 1.05,  outseam - rise * 0.28],
    [waistHalf * 1.05, outseam + seatLift],
    [waistHalf * 0.3, outseam + seatLift + 1], // back waist CB (extra rise)
    [0,               outseam - rise * 0.08 + seatLift], // back fork
    [hemHalf * 0.45,  0],
  ],

  // ── FLY FACING ───────────────────────────────────────────────────────────
  fly: ({ flyW, flyH }) => [
    [0, 0], [flyW, 0],
    [flyW, flyH * 0.85], [flyW * 0.5, flyH], [0, flyH],
  ],

  // ── NECK RIB (tshirt) ────────────────────────────────────────────────────
  neckRib: ({ halfNeck, ribH }) => [
    [0, 0], [halfNeck, 0], [halfNeck, ribH], [0, ribH],
  ],

  // ── POLO COLLAR RIB ──────────────────────────────────────────────────────
  poloCollar: ({ colW, colH }) => [
    [0, 0], [colW, 0], [colW * 1.05, colH * 0.5],
    [colW, colH], [0, colH],
  ],

  // ── POLO PLACKET ─────────────────────────────────────────────────────────
  placket: ({ plW, plH }) => [
    [0, 0], [plW, 0], [plW, plH], [0, plH],
  ],
};

// Convert polygon [[x,y]...] to tracedPoints [{x,y}...] format
const polyToTraced = (pts) => pts.map(([x, y]) => ({ x: +x.toFixed(2), y: +y.toFixed(2) }));

// ─── PRODUCT DEFINITIONS ─────────────────────────────────────────────────────

const PRODUCT_LIBRARY = {

  // ────────────────────────────────────────────────────────────────────────────
  shirt: {
    label: 'Formal / Casual Shirt',
    icon: '👔',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    // Base measurements (cm) at size M
    baseM: {
      chest: 104, waist: 98, hip: 106, shoulder: 44,
      bodyLength: 73, sleeveLength: 63, crownH: 17,
      bicep: 38, cuffWidth: 25.5, cuffHeight: 7,
      neckCirc: 40, collarFallH: 6, collarBandH: 3.5,
      yokeDepth: 10,
    },

    // Grading deltas per size step (cumulative from M)
    // Source: ISO 4416-1 + industry practice for men's dress shirts
    gradingSteps: {
      XS:  { chest:-8, waist:-8, hip:-8, shoulder:-2.4, bodyLength:-3, sleeveLength:-3, crownH:-0.8, bicep:-2.4, cuffWidth:-2, neckCirc:-4 },
      S:   { chest:-4, waist:-4, hip:-4, shoulder:-1.2, bodyLength:-1.5, sleeveLength:-1.5, crownH:-0.4, bicep:-1.2, cuffWidth:-1, neckCirc:-2 },
      M:   {},
      L:   { chest:+4, waist:+4, hip:+4, shoulder:+1.2, bodyLength:+1.5, sleeveLength:+1.5, crownH:+0.4, bicep:+1.2, cuffWidth:+1, neckCirc:+2 },
      XL:  { chest:+8, waist:+8, hip:+8, shoulder:+2.4, bodyLength:+3,   sleeveLength:+3,   crownH:+0.8, bicep:+2.4, cuffWidth:+2, neckCirc:+4 },
      XXL: { chest:+12,waist:+12,hip:+12,shoulder:+3.6, bodyLength:+4.5, sleeveLength:+4.5, crownH:+1.2, bicep:+3.6, cuffWidth:+3, neckCirc:+6 },
    },

    buildParts: (m) => [
      {
        id: 'shirt-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/4+1, height: m.bodyLength, shoulder: m.shoulder/2, neckWidth: m.neckCirc/6, armholeDrop: m.chest*0.22 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+1, halfWaist: m.waist/4+0.5, halfHip: m.hip/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neckCirc/6, armDrop: m.chest*0.22, frontNeck: 2.5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/4+1, height: m.bodyLength - m.yokeDepth, shoulder: m.shoulder/2, armholeDrop: m.chest*0.22 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+1, halfWaist: m.waist/4+0.5, halfHip: m.hip/4+0.5,
          bodyLen: m.bodyLength - m.yokeDepth, halfShoulder: m.shoulder/2,
          neckW: m.neckCirc/7, armDrop: m.chest*0.22, frontNeck: 1, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, cuffWidth: m.cuffWidth, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.cuffWidth, bicepW: m.bicep+2, crownH: m.crownH, length: m.sleeveLength,
        })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-collar', name: 'Collar Fall', category: 'collar',
        measurements: { width: m.neckCirc/2+1, height: m.collarFallH },
        tracedPoints: polyToTraced(SHAPES.collarFall({ halfNeck: m.neckCirc/2+1, fallH: m.collarFallH, spread: 1.2 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-band', name: 'Collar Band', category: 'collar',
        measurements: { width: m.neckCirc/2+1.5, height: m.collarBandH },
        tracedPoints: polyToTraced(SHAPES.band({ halfNeck: m.neckCirc/2+1.5, bandH: m.collarBandH })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-cuff', name: 'Cuff', category: 'cuff',
        measurements: { width: m.cuffWidth+1.5, height: m.cuffHeight },
        tracedPoints: polyToTraced(SHAPES.cuff({ cuffW: m.cuffWidth+1.5, cuffH: m.cuffHeight })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-yoke', name: 'Back Yoke', category: 'yoke',
        measurements: { width: m.shoulder/2+1, height: m.yokeDepth },
        tracedPoints: polyToTraced(SHAPES.yoke({ halfShoulder: m.shoulder/2+1, yokeDepth: m.yokeDepth, neckW: m.neckCirc/7 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'shirt-pocket', name: 'Chest Pocket', category: 'pocket',
        measurements: { width: 12, height: 12 },
        tracedPoints: polyToTraced(SHAPES.pocket({ pW: 12, pH: 12 })),
        seamAllowances: { top: 1, bottom: 2, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  trouser: {
    label: 'Trousers / Pants',
    icon: '👖',
    baseSize: '32',
    sizes: ['28', '30', '32', '34', '36', '38'],

    baseM: {
      waist: 82, hip: 100, thigh: 58, knee: 44, legOpening: 42,
      outseam: 102, rise: 28, waistbandH: 4,
    },

    gradingSteps: {
      '28': { waist:-10, hip:-6, thigh:-4, knee:-2, legOpening:-2, outseam:-2, rise:-1 },
      '30': { waist:-5,  hip:-3, thigh:-2, knee:-1, legOpening:-1, outseam:-1, rise:-0.5 },
      '32': {},
      '34': { waist:+5,  hip:+3, thigh:+2, knee:+1, legOpening:+1, outseam:+1, rise:+0.5 },
      '36': { waist:+10, hip:+6, thigh:+4, knee:+2, legOpening:+2, outseam:+2, rise:+1 },
      '38': { waist:+15, hip:+9, thigh:+6, knee:+3, legOpening:+3, outseam:+3, rise:+1.5 },
    },

    buildParts: (m) => [
      {
        id: 'trs-front', name: 'Front Panel', category: 'front',
        measurements: { waistWidth: m.waist/4+1, hipWidth: m.hip/4+1, thighWidth: m.thigh/4+0.5, height: m.outseam },
        tracedPoints: polyToTraced(SHAPES.trouserFront({
          waistHalf: m.waist/4+1, hipHalf: m.hip/4+1, thighHalf: m.thigh/4+0.5,
          kneeHalf: m.knee/4+0.5, hemHalf: m.legOpening/4+0.5,
          rise: m.rise, outseam: m.outseam,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1.5, right: 1.5 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'trs-back', name: 'Back Panel', category: 'back',
        measurements: { waistWidth: m.waist/4+2, hipWidth: m.hip/4+2, height: m.outseam + 3 },
        tracedPoints: polyToTraced(SHAPES.trouserBack({
          waistHalf: m.waist/4+2, hipHalf: m.hip/4+2, thighHalf: m.thigh/4+1.5,
          kneeHalf: m.knee/4+1, hemHalf: m.legOpening/4+1,
          rise: m.rise, outseam: m.outseam, seatLift: 3,
        })),
        seamAllowances: { top: 1, bottom: 4, left: 1.5, right: 1.5 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'trs-waist', name: 'Waistband', category: 'waistband',
        measurements: { width: m.waist/2+5, height: m.waistbandH*2 },
        tracedPoints: polyToTraced(SHAPES.waistband({ halfWaist: m.waist/2+5, bandH: m.waistbandH*2 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 0 },
      },
      {
        id: 'trs-fly', name: 'Fly Facing', category: 'fly',
        measurements: { width: 5.5, height: m.rise * 0.75 },
        tracedPoints: polyToTraced(SHAPES.fly({ flyW: 5.5, flyH: m.rise * 0.75 })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  tshirt: {
    label: 'T-Shirt',
    icon: '👕',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    baseM: {
      chest: 100, bodyLength: 68, shoulder: 42,
      sleeveLength: 22, crownH: 14, bicep: 36,
      neck: 38, armholeDrop: 20, neckRibH: 4,
    },

    gradingSteps: {
      XS:  { chest:-10, bodyLength:-4, shoulder:-3, sleeveLength:-3, bicep:-3, neck:-2, armholeDrop:-1 },
      S:   { chest:-5,  bodyLength:-2, shoulder:-1.5, sleeveLength:-1.5, bicep:-1.5, neck:-1, armholeDrop:-0.5 },
      M:   {},
      L:   { chest:+5,  bodyLength:+2, shoulder:+1.5, sleeveLength:+1.5, bicep:+1.5, neck:+1, armholeDrop:+0.5 },
      XL:  { chest:+10, bodyLength:+4, shoulder:+3, sleeveLength:+3, bicep:+3, neck:+2, armholeDrop:+1 },
      XXL: { chest:+15, bodyLength:+6, shoulder:+4.5, sleeveLength:+4.5, bicep:+4.5, neck:+3, armholeDrop:+1.5 },
    },

    buildParts: (m) => [
      {
        id: 'tsh-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength, shoulder: m.shoulder/2, neck: m.neck/2 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/6, armDrop: m.armholeDrop, frontNeck: 3.5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength, shoulder: m.shoulder/2 },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/7, armDrop: m.armholeDrop, frontNeck: 1, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.bicep * 0.82, bicepW: m.bicep+2, crownH: m.crownH,
          length: m.sleeveLength, isShort: true,
        })),
        seamAllowances: { top: 1, bottom: 2.5, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'tsh-rib', name: 'Neck Rib', category: 'trim',
        measurements: { width: m.neck/2, height: m.neckRibH * 2 },
        tracedPoints: polyToTraced(SHAPES.neckRib({ halfNeck: m.neck/2, ribH: m.neckRibH * 2 })),
        seamAllowances: { top: 0.7, bottom: 0.7, left: 0.7, right: 0.7 },
        grainLine: { angleDeg: 0 },
      },
    ],
  },

  // ────────────────────────────────────────────────────────────────────────────
  polo: {
    label: 'Polo T-Shirt',
    icon: '🏌️',
    baseSize: 'M',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],

    baseM: {
      chest: 102, bodyLength: 70, shoulder: 43,
      sleeveLength: 24, crownH: 14, bicep: 37,
      neck: 39, armholeDrop: 20,
      collarW: 38, collarH: 7,
      placketW: 3.5, placketH: 18,
    },

    gradingSteps: {
      XS:  { chest:-10, bodyLength:-4, shoulder:-3, sleeveLength:-3, bicep:-3, neck:-2, collarW:-2 },
      S:   { chest:-5,  bodyLength:-2, shoulder:-1.5, sleeveLength:-1.5, bicep:-1.5, neck:-1, collarW:-1 },
      M:   {},
      L:   { chest:+5,  bodyLength:+2, shoulder:+1.5, sleeveLength:+1.5, bicep:+1.5, neck:+1, collarW:+1 },
      XL:  { chest:+10, bodyLength:+4, shoulder:+3,   sleeveLength:+3,   bicep:+3,   neck:+2, collarW:+2 },
      XXL: { chest:+15, bodyLength:+6, shoulder:+4.5, sleeveLength:+4.5, bicep:+4.5, neck:+3, collarW:+3 },
    },

    buildParts: (m) => [
      {
        id: 'pol-front', name: 'Front Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/6+0.5, armDrop: m.armholeDrop, frontNeck: 5, isFront: true,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-back', name: 'Back Body', category: 'body',
        measurements: { width: m.chest/2, height: m.bodyLength },
        tracedPoints: polyToTraced(SHAPES.body({
          halfChest: m.chest/4+0.5, halfWaist: m.chest/4, halfHip: m.chest/4+0.5,
          bodyLen: m.bodyLength, halfShoulder: m.shoulder/2,
          neckW: m.neck/7, armDrop: m.armholeDrop, frontNeck: 1.5, isFront: false,
        })),
        seamAllowances: { top: 1, bottom: 3, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-sleeve', name: 'Sleeve', category: 'sleeve',
        measurements: { width: m.bicep+2, height: m.sleeveLength, crownH: m.crownH },
        tracedPoints: polyToTraced(SHAPES.sleeve({
          cuffW: m.bicep * 0.84, bicepW: m.bicep+2, crownH: m.crownH,
          length: m.sleeveLength, isShort: true,
        })),
        seamAllowances: { top: 1, bottom: 2.5, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
      {
        id: 'pol-collar', name: 'Collar Rib', category: 'collar',
        measurements: { width: m.collarW, height: m.collarH },
        tracedPoints: polyToTraced(SHAPES.poloCollar({ colW: m.collarW, colH: m.collarH })),
        seamAllowances: { top: 0.7, bottom: 0.7, left: 0.7, right: 0.7 },
        grainLine: { angleDeg: 0 },
      },
      {
        id: 'pol-placket', name: 'Front Placket', category: 'trim',
        measurements: { width: m.placketW*2, height: m.placketH },
        tracedPoints: polyToTraced(SHAPES.placket({ plW: m.placketW*2, plH: m.placketH })),
        seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: { angleDeg: 90 },
      },
    ],
  },
};

// Build a full set of NPF pattern objects from a product at a given base size
const buildProductPatterns = (productKey, styleId, styleName) => {
  const product = PRODUCT_LIBRARY[productKey];
  if (!product) return [];
  const m = { ...product.baseM };
  return product.buildParts(m).map(part => ({
    format: 'npf-v1',
    id:     `${productKey}-${part.id}-${Date.now()}`,
    partId: `${styleId}-${part.id}`,
    partName:    part.name,
    category:    part.category,
    product:     productKey,
    template:    part.id,
    measurements: part.measurements,
    tracedPoints: part.tracedPoints,
    seamAllowances: part.seamAllowances,
    grainLine:   part.grainLine,
    notches: [], darts: [], measurementPoints: [],
    gradingRules: {},
    specMapping: {}, brandRules: [], svgOutline: '',
    createdOn: new Date().toISOString(), updatedOn: new Date().toISOString(),
  }));
};

// Get grading rules in MarkerMaker's internal format { patId: { size: { key: delta } } }
const buildProductGradingRules = (productKey, patterns) => {
  const product = PRODUCT_LIBRARY[productKey];
  if (!product) return {};
  const rules = {};
  patterns.forEach(pat => {
    rules[pat.id] = {};
    Object.entries(product.gradingSteps).forEach(([sz, deltas]) => {
      rules[pat.id][sz] = { ...deltas };
    });
  });
  return rules;
};

// ============================================================================
//  STEP WIZARD HEADER
// ============================================================================
const STEPS = [
  { id: 1, label: 'Style',      icon: '🗂️' },
  { id: 2, label: 'Patterns',   icon: '📐' },
  { id: 3, label: 'Spec Sheet', icon: '📋' },
  { id: 4, label: 'Cross-Check',icon: '✅' },
  { id: 5, label: 'Edit',       icon: '✏️' },
  { id: 6, label: 'Grading',    icon: '📏' },
  { id: 7, label: 'Seam Check', icon: '🔗' },
  { id: 8, label: 'Marker',     icon: '🗺️' },
];

const WizardHeader = ({ step, onStepClick }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '10px 16px', background: 'white', borderBottom: '1px solid #e2e8f0', overflowX: 'auto' }}>
    {STEPS.map((s, i) => (
      <React.Fragment key={s.id}>
        <button
          onClick={() => onStepClick(s.id)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 2, padding: '6px 10px', borderRadius: 8, border: 'none',
            background: step === s.id ? '#1e293b' : step > s.id ? '#dcfce7' : '#f8fafc',
            color: step === s.id ? 'white' : step > s.id ? '#166534' : '#94a3b8',
            cursor: 'pointer', minWidth: 64, flexShrink: 0, fontSize: 9,
            fontWeight: step === s.id ? 'bold' : 'normal',
          }}
        >
          <span style={{ fontSize: 14 }}>{step > s.id ? '✓' : s.icon}</span>
          <span>{s.label}</span>
        </button>
        {i < STEPS.length - 1 && (
          <ChevronRight size={12} color={step > s.id + 1 ? '#22c55e' : '#cbd5e1'} style={{ flexShrink: 0 }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ============================================================================
//  STEP 1 — STYLE SELECTOR
//  Same filter categories as Workstation.js (brand, season, product, fabric)
// ============================================================================
const StyleSelector = ({ db, onSelect }) => {
  // ── Self-loading: fetch directly if parent db hasn't populated yet ─────────
  // Root cause of "styles not showing": db.masterStyles doesn't exist;
  // App.js stores styles as db.mgmtStyles. Also fetch from API as fallback.
  const [localStyles, setLocalStyles]       = useState(db?.mgmtStyles || []);
  const [stylesLoading, setStylesLoading]   = useState(false);
  const [stylesError, setStylesError]       = useState('');

  useEffect(() => {
    // Use parent db if already populated
    const fromDb = db?.mgmtStyles || [];
    if (fromDb.length > 0) { setLocalStyles(fromDb); return; }
    // Otherwise fetch directly — covers the case where MarkerMaker renders
    // before App.js finishes loading get_all_data.php
    setStylesLoading(true);
    setStylesError('');
    fetch(`${API_BASE}/get_all_data.php`)
      .then(r => r.json())
      .then(data => {
        const s = data.mgmtStyles || data.masterStyles || [];
        if (s.length === 0) setStylesError('No styles found in database. Save a style from Workstation first.');
        setLocalStyles(s);
      })
      .catch(() => setStylesError('Could not connect to database. Check API URL.'))
      .finally(() => setStylesLoading(false));
  }, []); // eslint-disable-line

  // Keep in sync if parent db updates after mount
  useEffect(() => {
    if ((db?.mgmtStyles || []).length > 0) setLocalStyles(db.mgmtStyles);
  }, [db?.mgmtStyles]);

  const styles = localStyles;
  const [filters, setFilters] = useState({ brand: '', season: '', product: '', fabric: '', search: '' });

  const getField = (s, fields, fallback = '') =>
    fields.map(f => s[f]).find(v => v) || fallback;

  const filtered = useMemo(() => styles.filter(s => {
    const brand   = getField(s, ['brand','brandName','Brand'], '');
    const season  = getField(s, ['season','Season','seasonCode'], '');
    const product = getField(s, ['productType','product','garmentType','garmentCategory','category'], '');
    const fabric  = getField(s, ['fabricType','fabric','fabricComposition','material'], '');
    const text    = [s.name || '', s.styleNo || '', s.styleCode || ''].join(' ').toLowerCase();
    return (
      (!filters.brand   || brand.toLowerCase().includes(filters.brand.toLowerCase())) &&
      (!filters.season  || season.toLowerCase().includes(filters.season.toLowerCase())) &&
      (!filters.product || product.toLowerCase().includes(filters.product.toLowerCase())) &&
      (!filters.fabric  || fabric.toLowerCase().includes(filters.fabric.toLowerCase())) &&
      (!filters.search  || text.includes(filters.search.toLowerCase()))
    );
  }), [styles, filters]);

  // Derive unique options for each filter
  const options = useMemo(() => ({
    brand:   [...new Set(styles.map(s => getField(s, ['brand','brandName','Brand'])).filter(Boolean))],
    season:  [...new Set(styles.map(s => getField(s, ['season','Season','seasonCode'])).filter(Boolean))],
    product: [...new Set(styles.map(s => getField(s, ['productType','product','garmentType'])).filter(Boolean))],
    fabric:  [...new Set(styles.map(s => getField(s, ['fabricType','fabric','material'])).filter(Boolean))],
  }), [styles]);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', marginBottom: 12, color: '#1e293b' }}>
        🗂️ Select a Style to load patterns
      </div>

      {/* Filter row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12, background: '#f0f9ff', padding: 10, borderRadius: 8 }}>
        <div>
          <label style={{ fontSize: 9, fontWeight: 'bold', color: '#0c4a6e', textTransform: 'uppercase' }}>Search</label>
          <input value={filters.search} onChange={e => setF('search', e.target.value)} placeholder="Style name / code…"
            style={{ width: '100%', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }} />
        </div>
        {['brand', 'season', 'product', 'fabric'].map(k => (
          <div key={k}>
            <label style={{ fontSize: 9, fontWeight: 'bold', color: '#0c4a6e', textTransform: 'uppercase' }}>{k}</label>
            <select value={filters[k]} onChange={e => setF(k, e.target.value)}
              style={{ width: '100%', padding: '5px 7px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }}>
              <option value="">All</option>
              {(options[k] || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        ))}
      </div>

      {/* Loading / error state */}
      {stylesLoading && (
        <div style={{ textAlign:'center', padding:30, color:'#7c3aed', fontSize:12 }}>
          ⟳ Loading styles from database…
        </div>
      )}
      {stylesError && !stylesLoading && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:12, marginBottom:10, fontSize:11, color:'#dc2626' }}>
          ⚠ {stylesError}
        </div>
      )}

      {/* Results */}
      {!stylesLoading && filtered.length === 0 && !stylesError ? (
        <div style={{ textAlign: 'center', color: '#94a3b8', padding: 40, fontSize: 12 }}>
          No styles found. Adjust filters or make sure styles are saved via Workstation.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
          {filtered.map(s => (
            <button key={s.id} onClick={() => onSelect(s)}
              style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              {s.previewImage && <img src={s.previewImage} alt="" style={{ width: '100%', height: 80, objectFit: 'contain', marginBottom: 6, borderRadius: 4 }} />}
              <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1e293b', marginBottom: 3 }}>{s.name || s.styleCode || 'Untitled Style'}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>
                {getField(s, ['brand','brandName']) && <span style={{ marginRight: 6 }}>🏷 {getField(s, ['brand','brandName'])}</span>}
                {getField(s, ['season','seasonCode']) && <span>📅 {getField(s, ['season','seasonCode'])}</span>}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
                {(s.parts || []).length} part(s) · {(s.patterns || []).length} pattern(s) stored
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 2 — PATTERN LOADER
//  Shows all patterns stored with the selected style. Allows adding missing
//  patterns from PartDNA or creating blank ones.
// ============================================================================
const PatternLoader = ({ style, db, patterns, setPatterns, gradingRules, setGradingRules }) => {
  const [localParts, setLocalParts] = useState(db?.masterParts || []);
  const [loading, setLoading]       = useState(false);
  const [loadMsg, setLoadMsg]       = useState('');
  const [showLibrary, setShowLibrary] = useState(false);

  // Self-load parts if parent db hasn't populated them yet
  useEffect(() => {
    if ((db?.masterParts || []).length > 0) { setLocalParts(db.masterParts); return; }
    fetch(`${API_BASE}/get_all_data.php`)
      .then(r => r.json())
      .then(data => setLocalParts(data.parts || data.masterParts || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if ((db?.masterParts || []).length > 0) setLocalParts(db.masterParts);
  }, [db?.masterParts]);

  const masterParts = localParts;

  // Fetch patterns from part_patterns table for all parts in this style.
  // Uses get_patterns.php?style_id=X which reads the style's workstation_data
  // to find part IDs, then returns all their stored patterns.
  useEffect(() => {
    if (!style?.id) return;
    setLoading(true);
    setLoadMsg('Loading patterns from database…');
    fetch(`${API_BASE}/get_patterns.php?style_id=${style.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.patterns?.length > 0) {
          setPatterns(data.patterns);
          setLoadMsg(`✓ ${data.patterns.length} pattern(s) loaded from part library`);
        } else {
          setLoadMsg(`No patterns found in DB for this style's parts. Add below or import .npf files.`);
        }
      })
      .catch(() => setLoadMsg('Could not reach DB — add patterns manually below.'))
      .finally(() => setLoading(false));
  }, [style?.id]);

  const addFromPart = (part) => {
    const exists = patterns.find(p => p.partId === part.id);
    if (exists) { alert(`Pattern for "${part.name}" already loaded.`); return; }
    const newPat = createEmptyPattern(
      part.id, part.name,
      part.specs?.template || '',
      part.specs?.measurements || {},
    );
    setPatterns(prev => [...prev, newPat]);
  };

  const removePattern = (patId) => {
    if (!window.confirm('Remove this pattern?')) return;
    setPatterns(prev => prev.filter(p => p.id !== patId));
  };

  const importNpf = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const pat = JSON.parse(ev.target.result);
        if (pat.format !== 'npf-v1') throw new Error('Not a valid .npf file');
        setPatterns(prev => [...prev, { ...pat, id: `pat-${Date.now()}` }]);
      } catch (err) {
        alert('Import failed: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Parts associated with this style that don't have a pattern yet
  const styleParts = useMemo(() => {
    const stylePartNames = (style?.parts || []).map(p => p.name?.toLowerCase());
    return masterParts.filter(mp =>
      stylePartNames.some(n => mp.name?.toLowerCase().includes(n) || n.includes(mp.name?.toLowerCase()))
    );
  }, [style, masterParts]);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4 }}>
        📐 Patterns for: <span style={{ color: '#7c3aed' }}>{style?.name || style?.styleCode}</span>
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
        {loading
          ? <span style={{ color: '#7c3aed' }}>⟳ {loadMsg}</span>
          : <span style={{ color: loadMsg.startsWith('✓') ? '#059669' : '#64748b' }}>{loadMsg || `${patterns.length} pattern(s) loaded.`}</span>}
      </div>

      {/* ── PRODUCT LIBRARY PANEL ── */}
      {showLibrary ? (
        <div style={{ background: '#0f172a', borderRadius: 10, padding: 16, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: 'white', fontWeight: 'bold', fontSize: 13 }}>📚 Product Library — select a product to load all standard patterns</span>
            <button onClick={() => setShowLibrary(false)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
            {Object.entries(PRODUCT_LIBRARY).map(([key, prod]) => (
              <button key={key} onClick={() => {
                const newPats = buildProductPatterns(key, style?.id || 'new', style?.name || '');
                const newRules = buildProductGradingRules(key, newPats);
                setPatterns(newPats);
                if (setGradingRules) setGradingRules(newRules);
                setShowLibrary(false);
                setLoadMsg(`✓ ${newPats.length} standard patterns loaded for ${prod.label}`);
              }}
                style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '14px 12px',
                  cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#7c3aed'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{prod.icon}</div>
                <div style={{ fontWeight: 'bold', fontSize: 12, color: 'white', marginBottom: 3 }}>{prod.label}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>
                  Base size {prod.baseSize} · {prod.buildParts(prod.baseM).length} pattern pieces
                </div>
                <div style={{ fontSize: 9, color: '#6366f1', marginTop: 3 }}>
                  {prod.sizes.join(' · ')}
                </div>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: '#64748b', borderTop: '1px solid #1e293b', paddingTop: 8 }}>
            ℹ Patterns load with industry-standard measurements, correct polygon shapes, and pre-configured grading rules (ISO/AATCC basis).
            You can add custom parts below or edit any pattern in Step 5.
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '10px 14px' }}>
          <span style={{ fontSize: 14 }}>📚</span>
          <span style={{ fontSize: 11, color: '#4c1d95', flex: 1 }}>
            Load all standard pattern pieces, shapes and grading rules from the product library:
          </span>
          <button onClick={() => setShowLibrary(true)}
            style={{ padding: '7px 16px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold', flexShrink: 0 }}>
            📚 Load from Library
          </button>
        </div>
      )}

      {/* Loaded patterns grid */}
      {!showLibrary && patterns.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 16 }}>
          {patterns.map(p => (
            <div key={p.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, textAlign: 'center' }}>
              <PatternSvg pattern={p} width={140} height={120} />
              <div style={{ fontSize: 10, fontWeight: 'bold', marginTop: 4, color: '#1e293b' }}>{p.partName}</div>
              <div style={{ fontSize: 9, color: '#94a3b8' }}>{p.template}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4, justifyContent: 'center' }}>
                <button onClick={() => exportPatternFile(p)} title="Export .npf"
                  style={{ padding: '2px 6px', fontSize: 9, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4, cursor: 'pointer', color: '#15803d' }}>
                  ↓ NPF
                </button>
                <button onClick={() => removePattern(p.id)}
                  style={{ padding: '2px 6px', fontSize: 9, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', color: '#dc2626' }}>
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add from PartDNA */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 'bold', color: '#475569', marginBottom: 8 }}>
          Add patterns from Part DNA library:
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {masterParts.filter(p => p.specs?.template).map(p => (
            <button key={p.id} onClick={() => addFromPart(p)}
              style={{
                padding: '5px 10px', fontSize: 10, background: 'white', border: '1px solid #cbd5e1',
                borderRadius: 20, cursor: 'pointer', color: '#334155',
                opacity: patterns.find(pt => pt.partId === p.id) ? 0.4 : 1,
              }}>
              {patterns.find(pt => pt.partId === p.id) ? '✓ ' : '+ '}{p.name}
            </button>
          ))}
          {masterParts.filter(p => p.specs?.template).length === 0 && (
            <span style={{ fontSize: 11, color: '#94a3b8' }}>No spec-built parts in Part DNA yet.</span>
          )}
        </div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '5px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 10, color: '#1d4ed8' }}>
          <Upload size={12} /> Import .npf file
          <input type="file" accept=".npf,.json" hidden onChange={importNpf} />
        </label>
        {/* ── Load standard cut parts for a product ── */}
        {(() => {
          const [selProd, setSelProd] = React.useState('');
          return (
            <div style={{ marginTop:8, display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', padding:'8px 10px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6 }}>
              <span style={{ fontSize:10, fontWeight:'bold', color:'#166534' }}>📋 Load standard pieces:</span>
              <select value={selProd} onChange={e=>setSelProd(e.target.value)}
                style={{ padding:'4px 8px', border:'1px solid #bbf7d0', borderRadius:4, fontSize:10, background:'white' }}>
                <option value="">── Product ──</option>
                {Object.entries(GARMENT_STANDARDS).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={()=>{
                if (!selProd) return;
                const pcs = buildStandardPatterns(selProd, 'M', '');
                setPatterns(pcs);
              }} disabled={!selProd}
                style={{ padding:'4px 12px', background:selProd?'#166534':'#94a3b8', color:'white', border:'none', borderRadius:4, cursor:selProd?'pointer':'not-allowed', fontSize:10, fontWeight:'bold' }}>
                Load {selProd ? GARMENT_STANDARDS[selProd]?.pieces.length + ' pieces' : ''}
              </button>
              <span style={{ fontSize:9, color:'#64748b' }}>Loads standard cut parts with base (M) measurements + notch positions pre-set</span>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// ============================================================================
//  STEP 3 — SPEC SHEET IMPORTER
//  Import from Style Registry (CSV / JSON / paste). Parses and stores.
// ============================================================================
const SpecSheetImporter = ({ style, specData, setSpecData, onImportComplete }) => {
  const [parsed,      setParsed]      = useState(null);    // full parsed result
  const [unitMode,    setUnitMode]    = useState('auto');  // 'auto'|'in'|'cm'
  const [baseSize,    setBaseSize]    = useState('M');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // Convert a single value: inches→cm if needed
  const toDisplay = (v, isInches) => {
    const mode = unitMode === 'auto' ? (isInches ? 'in' : 'cm') : unitMode;
    return mode === 'in' ? +(v * 2.54).toFixed(2) : +v.toFixed(2);
  };
  const unitLabel = (isInches) =>
    (unitMode === 'auto' ? (isInches ? 'in→cm' : 'cm') : (unitMode === 'in' ? 'in→cm' : 'cm'));

  const doParse = (text) => {
    setError('');
    const result = parseSpecSheet(text);
    if (!result || Object.keys(result.base || {}).length === 0) {
      setError('Could not parse. Make sure it is a valid spec sheet CSV, or paste key: value lines.');
      return;
    }
    setParsed(result);
    setBaseSize(result.baseSize || 'M');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = ev => { doParse(ev.target.result); setLoading(false); };
    reader.onerror = () => { setError('File read error.'); setLoading(false); };
    reader.readAsText(f);
    e.target.value = '';
  };

  // Converts Style Registry's row-table measurements
  // ({ headers:['pom','tolerance','S','M','L',...], rows:[{pom,tolerance,S,M,L,...}] })
  // into the { base, sizeData, sizes, meta, isInches } shape the rest of this
  // importer (confirm(), SpecCrossCheck, etc.) already expects — the same
  // shape produced by the CSV branch of parseSpecSheet().
  const parseRegistryMeasurements = (ms) => {
    setError('');
    const SIZE_RE = /^(XXS|XS|S|M|L|XL|2XL|XXL|3XL|XXXL|\d{2}|\d{2}\/\d{2})$/i;
    const headers = (ms.headers || []).filter(h => h !== 'pom' && h !== 'tolerance');
    const sizes   = headers.filter(h => SIZE_RE.test(String(h).trim()));
    if (sizes.length === 0) {
      setError('Could not parse stored spec sheet — no recognizable size columns.');
      return;
    }

    const sizeData = {}; sizes.forEach(sz => (sizeData[sz] = {}));
    const tols = {};
    (ms.rows || []).forEach(row => {
      const name = row.pom || row[Object.keys(row)[0]] || '';
      if (!name) return;
      const t = parseFloat(row.tolerance);
      if (!isNaN(t)) tols[name] = t;
      sizes.forEach(sz => {
        const v = parseFloat(row[sz]);
        if (!isNaN(v)) sizeData[sz][name] = v;
      });
    });

    const baseSz = sizes.includes('M') ? 'M' : sizes[Math.floor(sizes.length / 2)];
    const base = { ...sizeData[baseSz] };
    const sample = Object.values(base)[0] || 100;
    const isInches = sample > 0 && sample < 50;

    const result = { base, sizeData, sizes, tolerances: tols, meta: {}, baseSize: baseSz, isInches, _format: 'registry_measurements' };
    setParsed(result);
    setBaseSize(result.baseSize || 'M');
  };

  const loadFromStyle = async () => {
    if (!style?.id) return;
    setLoading(true); setError('');
    try {
      // Style Registry never writes a 'spec_sheet' category — graded
      // measurements live on the style record itself as `style.measurements`
      // ({ headers, rows }), saved under category 'style'. Re-fetch the style
      // fresh (in case it changed since this wizard loaded it), falling back
      // to the `style` prop we already have.
      let ms = style.measurements;
      try {
        const res = await fetch(`${LOAD_URL}?category=style&id=${style.id}`);
        const j   = await res.json();
        const fresh = j?.data?.measurements;
        if (fresh?.rows?.length) ms = fresh;
      } catch (_) { /* fall back to style.measurements below */ }

      if (!ms?.rows?.length) { setError('No spec sheet stored for this style yet — extract or add one in Style Registry first.'); return; }
      parseRegistryMeasurements(ms);
    } catch { setError('Could not load from registry.'); }
    finally { setLoading(false); }
  };

  const confirm = () => {
    if (!parsed) return;
    const isIn = unitMode === 'auto' ? parsed.isInches : unitMode === 'in';
    // Build base and sizeData in cm
    const base = {};
    Object.entries(parsed.base || {}).forEach(([k, v]) => { base[k] = toDisplay(v, isIn); });
    const sizeData = {};
    (parsed.sizes || []).forEach(sz => {
      sizeData[sz] = {};
      Object.entries(parsed.sizeData?.[sz] || {}).forEach(([k, v]) => { sizeData[sz][k] = toDisplay(v, isIn); });
    });
    const result = { base, sizes: sizeData, meta: parsed.meta || {}, sourceUnit: isIn ? 'in' : 'cm' };
    setSpecData(result);
    onImportComplete(result);
  };

  const sizes   = parsed?.sizes || [];
  const measKeys = Object.keys(parsed?.base || {});
  const isIn    = unitMode === 'auto' ? !!parsed?.isInches : unitMode === 'in';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 8 }}>
        📋 Spec Sheet Import — <span style={{ color: '#7c3aed' }}>{style?.name || style?.styleCode || '—'}</span>
      </div>

      {/* Actions row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={loadFromStyle} disabled={loading || !style?.id}
          style={{ padding: '7px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>
          {loading ? '⟳' : '⬇'} Load from Style Registry
        </button>
        <label style={{ padding: '7px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: '#15803d' }}>
          📂 Upload Spec Sheet (.csv / .txt / .json)
          <input type="file" accept=".csv,.json,.txt,.xls,.xlsx" hidden onChange={handleFile} />
        </label>
        <textarea
          rows={1}
          placeholder="Or paste raw CSV / key:value text here and press Enter…"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doParse(e.target.value); } }}
          style={{ flex: 1, minWidth: 200, padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, resize: 'none' }}
        />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '7px 12px', marginBottom: 10, fontSize: 11, color: '#dc2626' }}>
          ⚠ {error}
        </div>
      )}

      {parsed && (
        <>
          {/* Metadata strip */}
          {Object.keys(parsed.meta || {}).length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 12px' }}>
              {Object.entries(parsed.meta).map(([k, v]) => (
                <span key={k} style={{ fontSize: 10, color: '#475569' }}>
                  <strong style={{ color: '#1e293b' }}>{k}:</strong> {v}
                </span>
              ))}
            </div>
          )}

          {/* Controls */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#475569', fontWeight: 'bold' }}>
              ✓ {measKeys.length} measurements · {sizes.length} sizes
              {parsed.isInches && <span style={{ color: '#f59e0b', marginLeft: 6 }}>⚠ values detected in INCHES</span>}
            </span>
            {/* Unit toggle */}
            <div style={{ display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 6, padding: 3 }}>
              {[['auto', `Auto (${parsed.isInches ? 'in→cm' : 'cm'})`], ['in', 'Force in→cm'], ['cm', 'Keep as cm']].map(([v, label]) => (
                <button key={v} onClick={() => setUnitMode(v)}
                  style={{ padding: '3px 10px', background: unitMode === v ? '#2563eb' : 'transparent', color: unitMode === v ? 'white' : '#64748b', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 10 }}>
                  {label}
                </button>
              ))}
            </div>
            {/* Base size selector */}
            {sizes.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: '#64748b' }}>Base size:</span>
                <select value={baseSize} onChange={e => setBaseSize(e.target.value)}
                  style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11 }}>
                  {sizes.map(sz => <option key={sz} value={sz}>{sz}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Measurement table */}
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 12 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#1e293b', color: 'white' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', minWidth: 220, position: 'sticky', left: 0, background: '#1e293b' }}>
                    POINT OF MEASUREMENT
                  </th>
                  <th style={{ padding: '8px 8px', textAlign: 'center', color: '#94a3b8', width: 50 }}>TOL</th>
                  {sizes.map(sz => (
                    <th key={sz} style={{ padding: '8px 10px', textAlign: 'center', color: sz === baseSize ? '#34d399' : '#e2e8f0', minWidth: 70 }}>
                      {sz}{sz === baseSize ? ' ★' : ''}
                    </th>
                  ))}
                  {/* If no sizes, show single value column */}
                  {sizes.length === 0 && (
                    <th style={{ padding: '8px 10px', textAlign: 'center', color: '#34d399', minWidth: 80 }}>
                      Value ({unitLabel(isIn)})
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {measKeys.map((k, ri) => (
                  <tr key={k} style={{ background: ri % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 12px', color: '#1e293b', fontWeight: '500', position: 'sticky', left: 0, background: ri%2===0?'white':'#f8fafc' }}>{k}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: '#94a3b8', fontSize: 10 }}>
                      ±{parsed.tolerances?.[k] ?? '—'}
                    </td>
                    {sizes.length > 0 ? sizes.map(sz => {
                      const rawV = parsed.sizeData?.[sz]?.[k];
                      const dispV = rawV != null ? toDisplay(rawV, isIn) : '—';
                      const isBase = sz === baseSize;
                      const diff   = (rawV != null && parsed.base?.[k] != null && sz !== baseSize)
                        ? (rawV - (parsed.base?.[k] || 0)) : null;
                      return (
                        <td key={sz} style={{ padding: '6px 10px', textAlign: 'center',
                          color: isBase ? '#059669' : '#334155',
                          fontWeight: isBase ? 'bold' : 'normal',
                          background: isBase ? '#f0fdf4' : 'transparent' }}>
                          {dispV}
                          {diff !== null && diff !== 0 && (
                            <span style={{ fontSize: 9, color: diff > 0 ? '#2563eb' : '#dc2626', marginLeft: 3 }}>
                              ({diff > 0 ? '+' : ''}{diff.toFixed(2)})
                            </span>
                          )}
                        </td>
                      );
                    }) : (
                      <td style={{ padding: '6px 10px', textAlign: 'center', color: '#059669', fontWeight: 'bold' }}>
                        {toDisplay(parsed.base[k], isIn)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={confirm}
            style={{ padding: '10px 24px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
            ✓ Use This Spec Sheet — Proceed to Cross-Check →
          </button>
        </>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 4 — SPEC → PATTERN CROSS-CHECK
//  Compares spec-sheet measurements against stored pattern specs.
//  Applies brand rules to adjust.
// ============================================================================
const SpecCrossCheck = ({ patterns, specData, onAdjust }) => {
  const [results, setResults] = useState([]);
  const [adjustedPatterns, setAdjustedPatterns] = useState(patterns);
  const base = specData?.base || {};

  useEffect(() => {
    const res = patterns.map(pat => {
      const m = pat.measurements || {};
      const mapping = fuzzyMapSpec(base, Object.keys(m));
      const diffs = Object.entries(mapping).map(([specKey, patKey]) => ({
        specKey, patKey,
        specValue: base[specKey],
        patValue: m[patKey],
        delta: (base[specKey] || 0) - (m[patKey] || 0),
        status: Math.abs((base[specKey] || 0) - (m[patKey] || 0)) <= 0.5 ? 'ok' : 'mismatch',
      }));
      return { pattern: pat, diffs, mapping };
    });
    setResults(res);
    setAdjustedPatterns(patterns);
  }, [patterns, specData]);

  const applyAdjustment = (patIdx, specKey, patKey, newValue) => {
    setAdjustedPatterns(prev => prev.map((p, i) => {
      if (i !== patIdx) return p;
      return { ...p, measurements: { ...p.measurements, [patKey]: newValue }, updatedOn: new Date().toISOString() };
    }));
  };

  const applyAll = () => {
    let updated = [...patterns];
    results.forEach((r, patIdx) => {
      r.diffs.filter(d => d.status === 'mismatch').forEach(d => {
        updated[patIdx] = {
          ...updated[patIdx],
          measurements: { ...updated[patIdx].measurements, [d.patKey]: d.specValue },
          updatedOn: new Date().toISOString(),
        };
      });
    });
    setAdjustedPatterns(updated);
    onAdjust(updated);
  };

  const totalMismatches = results.reduce((s, r) => s + r.diffs.filter(d => d.status === 'mismatch').length, 0);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4 }}>
        ✅ Spec → Pattern Cross-Check
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
        Comparing {Object.keys(base).length} spec measurements against {patterns.length} patterns.
        {totalMismatches > 0 ? (
          <span style={{ color: '#dc2626', fontWeight: 'bold', marginLeft: 6 }}>⚠ {totalMismatches} mismatch(es) found.</span>
        ) : (
          <span style={{ color: '#16a34a', fontWeight: 'bold', marginLeft: 6 }}>✓ All measurements match.</span>
        )}
      </div>

      {totalMismatches > 0 && (
        <button onClick={applyAll}
          style={{ marginBottom: 12, padding: '7px 16px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
          ⚡ Auto-adjust all patterns to match spec sheet
        </button>
      )}

      {results.map((r, patIdx) => (
        <div key={r.pattern.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1e293b', marginBottom: 8 }}>{r.pattern.partName}</div>
          {r.diffs.length === 0 ? (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>No matching spec measurements found for this pattern.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Spec Field', 'Spec Value', 'Pattern Key', 'Pattern Value', 'Delta', 'Status', 'Adjust'].map(h => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontSize: 10, color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.diffs.map((d, di) => (
                  <tr key={di} style={{ borderBottom: '1px solid #f1f5f9', background: d.status === 'mismatch' ? '#fffbeb' : 'white' }}>
                    <td style={{ padding: '4px 8px' }}>{d.specKey}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>{d.specValue?.toFixed(1)}</td>
                    <td style={{ padding: '4px 8px', color: '#6366f1' }}>{d.patKey}</td>
                    <td style={{ padding: '4px 8px' }}>{d.patValue?.toFixed(1)}</td>
                    <td style={{ padding: '4px 8px', color: d.delta !== 0 ? '#f59e0b' : '#22c55e', fontWeight: 'bold' }}>
                      {d.delta > 0 ? '+' : ''}{d.delta.toFixed(1)}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {d.status === 'ok'
                        ? <span style={{ color: '#16a34a', fontSize: 10 }}>✓ OK</span>
                        : <span style={{ color: '#dc2626', fontSize: 10 }}>⚠ Mismatch</span>}
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      {d.status === 'mismatch' && (
                        <button onClick={() => applyAdjustment(patIdx, d.specKey, d.patKey, d.specValue)}
                          style={{ padding: '2px 8px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 9 }}>
                          Apply
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}

      <button onClick={() => onAdjust(adjustedPatterns)}
        style={{ padding: '8px 20px', background: '#1e293b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
        Confirm patterns & continue →
      </button>
    </div>
  );
};

// ============================================================================
//  STEP 5 — PATTERN EDITOR
//  • Uses tracedPoints (real shape) when available, generic shape as fallback
//  • Notch snaps to nearest polygon edge (correct position every time)
//  • Measurements stored as raw SVG coords — rendered directly, no transform
//  • Zoom / pan via SVG viewBox
//  • Inline spec editing
//  • Scale indicator
// ============================================================================

// ── Helpers shared by PatternEditor + PatternSvgInline ─────────────────────

// Build display polygon from a pattern.
// Returns { pts (cm, centred), W, H, hasTp } where pts is the polygon as [x,y] pairs.
// Case-insensitive, substring-match measurement lookup.
// Tries exact case-insensitive match first, then substring match.
// This means "Width"/"width"/"chest width"/"Bottom Width" all work.
const mGet = (m, ...keys) => {
  const entries = Object.entries(m || {});
  if (!entries.length) return undefined;
  for (const key of keys) {
    const kl = key.toLowerCase();
    const hit = entries.find(([k]) => k.toLowerCase() === kl);
    if (hit != null && hit[1] != null) return +hit[1];
  }
  for (const key of keys) {
    const kl = key.toLowerCase();
    const hit = entries.find(([k]) => k.toLowerCase().includes(kl));
    if (hit != null && hit[1] != null) return +hit[1];
  }
  return undefined;
};

// ── Quadratic Bezier sampler — generates n+1 smooth polygon points ──────────
const qbez = (p0, p1, p2, n = 8) => {
  const r = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n, q = 1 - t;
    r.push([q*q*p0[0]+2*q*t*p1[0]+t*t*p2[0], q*q*p0[1]+2*q*t*p1[1]+t*t*p2[1]]);
  }
  return r;
};

// ── Detect piece type from template string or part name ──────────────────────
const detectTemplate = (pattern) => {
  const t  = (pattern.template  || '').toLowerCase();
  const pn = (pattern.partName  || '').toLowerCase();
  const sm = (pattern.sourceMethod || '').toLowerCase();
  const s  = t + ' ' + pn + ' ' + sm;

  // ── Direct ShapeBuilder template passthrough ─────────────────────────────
  // If pattern.template is a known ShapeBuilder key, use ShapeBuilder's build()
  // function directly (most accurate — uses same math that created the shape).
  if (GARMENT_TEMPLATES[pattern.template]) return pattern.template;

  // ── ShapeBuilder template → MarkerMaker template name mapping ───────────
  const SB_MAP = {
    'tee_front':'body_front', 'tee_back':'body_back',
    'shirt_front':'body_front', 'shirt_back':'body_back',
    'pant_front':'trouser_front', 'pant_back':'trouser_back',
    'sleeve_set_in':'sleeve', 'sleeve':'sleeve',
    'collar':'collar', 'collar_stand':'collar_stand',
    'cuff':'cuff', 'yoke':'yoke',
  };
  if (SB_MAP[t]) return SB_MAP[t];
  if (/trouser.*(front|panel)|front.*(trouser|pant)/i.test(s)) return 'trouser_front';
  if (/trouser.*(back|panel)|back.*(trouser|pant)/i.test(s))  return 'trouser_back';
  if (/waistband|waist.band/i.test(s))                         return 'waistband';
  if (/pocket|bag/i.test(s))                                   return 'pocket';
  if (/collar[\s_-]?stand|neck[\s_-]?band/i.test(s))           return 'collar_stand';
  if (/polo.collar|polo/i.test(s))                             return 'collar_polo';
  if (/collar/i.test(s))                                       return 'collar';
  if (/cuff/i.test(s))                                         return 'cuff';
  if (/yoke/i.test(s))                                         return 'yoke';
  if (/placket|\bband\b/i.test(s))                             return 'placket';
  if (/\brib\b|neck[\s_-]?rib/i.test(s))                       return 'rib';
  if (/sleeve/i.test(s))                                       return 'sleeve';
  if (/back.*body|back.*panel|body.*back/i.test(s))            return 'body_back';
  if (/front.*body|front.*panel|body.*front/i.test(s))         return 'body_front';
  return 'generic';
};

// ═══════════════════════════════════════════════════════════════════════════
//  SHAPE GENERATORS
//  All return polygon points as [[x,y], ...] in cm, centred at x=0
//  y=0 = hem/bottom,  y=H = top/shoulder/waistband
//  Coordinate conventions match reference industry patterns.
// ═══════════════════════════════════════════════════════════════════════════

const shapeBodyFront = (m) => {
  // 'width' intentionally excluded — it matches narrow pieces (placket, cuff, collar).
  // For body shapes always use halfChest / chestWidth / chest.
  const rawW = mGet(m,'halfChest','chestWidth','chest width','chest') || 0;
  const W    = rawW >= 15 ? rawW : 26;   // min 15 cm half-chest; 26 cm default
  const H   = mGet(m,'bodyLength','height','length') || 74;
  const AD  = mGet(m,'armholeDepth') || Math.min(H * 0.32, 24);
  const SHf = (mGet(m,'shoulder','shoulderWidth') || W * 1.72);
  const SH  = SHf / 2;                                          // half shoulder
  const NW  = mGet(m,'neckWidth') || W * 0.34;
  const ND  = mGet(m,'neckDepth','neckDepthFront') || W * 0.32;
  const uY  = H - AD;  // underarm Y

  // Armhole: concave S-curve from underarm to shoulder (sampled Bezier)
  const armholeRight = [
    ...qbez([W, uY], [W + 1.2, uY + AD * 0.35], [W * 0.78, uY + AD * 0.65], 5),
    ...qbez([W * 0.78, uY + AD * 0.65], [W * 0.58, uY + AD * 0.88], [SH, H - 2], 5),
  ];
  // Neckline: quarter-circle arc from shoulder to CF
  const neckRight = qbez([NW, H - 0.5], [NW * 0.2, H + ND * 0.1], [0, H - ND], 6);

  const pts = [
    [-W, 0], [W, 0], [W, uY],
    ...armholeRight,
    [SH, H - 2], [SH * 0.85, H - 0.5],
    ...neckRight,
    ...neckRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-SH * 0.85, H - 0.5], [-SH, H - 2],
    ...armholeRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-W, uY],
  ];
  return { pts, W, H: H + ND * 0.3, hasTp: false, template: 'body_front',
    guides: [
      { y: uY, label: 'CHEST LINE', color: '#dc2626' },
      { y: H - AD - (mGet(m,'chestToWaist') || AD * 0.6), label: 'WAIST LINE', color: '#f59e0b' },
      { y: 0, label: 'HEM LINE', color: '#64748b' },
    ]
  };
};

const shapeBodyBack = (m) => {
  const rawW = mGet(m,'halfChest','chestWidth','chest width','chest') || 0;
  const W    = rawW >= 15 ? rawW : 26;
  const H   = mGet(m,'bodyLength','height','length') || 74;
  const AD  = mGet(m,'armholeDepth') || Math.min(H * 0.32, 24);
  const SHf = (mGet(m,'shoulder','shoulderWidth') || W * 1.72);
  const SH  = SHf / 2;
  const NW  = mGet(m,'neckWidth') || W * 0.345;
  const ND  = mGet(m,'neckDepth','neckDepthBack') || W * 0.095; // shallow back neck
  const uY  = H - AD;

  const armholeRight = [
    ...qbez([W, uY], [W + 1.0, uY + AD * 0.38], [W * 0.8, uY + AD * 0.65], 5),
    ...qbez([W * 0.8, uY + AD * 0.65], [W * 0.58, uY + AD * 0.88], [SH, H - 1.5], 5),
  ];
  const neckRight = qbez([NW, H - 0.3], [NW * 0.3, H - ND * 0.3], [0, H - ND], 4);

  const pts = [
    [-W, 0], [W, 0], [W, uY],
    ...armholeRight,
    [SH, H - 1.5], [SH * 0.88, H - 0.3],
    ...neckRight,
    ...neckRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-SH * 0.88, H - 0.3], [-SH, H - 1.5],
    ...armholeRight.slice().reverse().map(([x, y]) => [-x, y]),
    [-W, uY],
  ];
  return { pts, W, H: H + ND * 0.3, hasTp: false, template: 'body_back',
    guides: [
      { y: uY, label: 'CHEST LINE', color: '#dc2626' },
      { y: H - AD - (mGet(m,'chestToWaist') || AD * 0.6), label: 'WAIST LINE', color: '#f59e0b' },
      { y: 0, label: 'HEM LINE', color: '#64748b' },
    ]
  };
};

const shapeSleeve = (m) => {
  const W   = mGet(m,'width','halfSleeveWidth','sleeveWidth') || 19;  // half sleeve width
  const SL  = mGet(m,'sleeveLength','height','length') || 61;
  const CW  = mGet(m,'cuffWidth','cuffLength') || (W * 0.65);         // half cuff width
  const capH = W * 0.55;   // sleeve cap height ≈ 55% of sleeve width (standard)

  // Sleeve cap: convex bell curve — the most distinctive shape
  // Front cap (right): slightly steeper. Back cap (left): slightly shallower.
  const capRight = [
    ...qbez([W, SL], [W * 0.9, SL + capH * 0.4], [W * 0.35, SL + capH * 0.82], 6),
    ...qbez([W * 0.35, SL + capH * 0.82], [W * 0.1, SL + capH * 0.97], [0, SL + capH], 4),
  ];
  const capLeft = [
    ...qbez([0, SL + capH], [-W * 0.1, SL + capH * 0.97], [-W * 0.42, SL + capH * 0.78], 4),
    ...qbez([-W * 0.42, SL + capH * 0.78], [-W * 0.85, SL + capH * 0.4], [-W, SL], 6),
  ];

  const pts = [
    [-CW, 0], [CW, 0],
    ...qbez([CW, 0], [W * 0.85, SL * 0.3], [W, SL], 6),  // side seam right
    ...capRight,
    ...capLeft,
    ...qbez([-W, SL], [-W * 0.85, SL * 0.3], [-CW, 0], 6), // side seam left
  ];
  return { pts, W, H: SL + capH, hasTp: false, template: 'sleeve',
    guides: [
      { y: SL, label: 'SLEEVE WIDTH', color: '#dc2626' },
      { y: SL + capH * 0.5, label: 'CAP MIDPOINT', color: '#f59e0b' },
      { y: 0, label: 'CUFF LINE', color: '#64748b' },
    ]
  };
};

const shapeTrouserFront = (m) => {
  const IS  = mGet(m,'inseam') || 81;
  const FR  = mGet(m,'frontRise','rise') || 28;
  const HW  = mGet(m,'halfWaist','waist') || 40;
  const HH  = mGet(m,'halfHip','hip') || 50;
  const HT  = mGet(m,'halfThigh','thigh') || 30;
  const HK  = mGet(m,'halfKnee','knee') || 21;
  const HHm = mGet(m,'halfHem','hem') || 21.5;
  const H   = FR + IS;
  const hipY = IS;
  const kY  = IS * 0.38;

  // Crotch J-curve: most distinctive trouser feature
  // Extends outward (negative x = inseam/crotch side)
  const crotch = [
    ...qbez([-HW * 0.02, H + 1.2], [-FR * 0.08, H + 0.3], [-FR * 0.18, H - FR * 0.12], 3),
    ...qbez([-FR * 0.18, H - FR * 0.12], [-FR * 0.38, H - FR * 0.35], [-FR * 0.32, H - FR * 0.62], 5),
    ...qbez([-FR * 0.32, H - FR * 0.62], [-FR * 0.22, H - FR * 0.82], [-HT * 0.65, IS], 4),
  ];
  // Side seam: slight curve at hip
  const sideRight = qbez([HHm, 0], [HH * 0.98, kY * 1.2], [HH, hipY], 5);
  const inseamLeft = qbez([-HT * 0.65, IS], [-HK * 0.96, kY], [-HHm, 0], 5);

  const pts = [
    [HHm, 0],
    ...sideRight,
    [HH, hipY],
    ...qbez([HH, hipY], [HW * 1.0, H - 2], [HW * 0.98, H], 4),
    [HW * 0.98, H],
    ...crotch,
    ...inseamLeft,
    [-HHm, 0],
  ];
  return { pts, W: HH * 1.02, H: H + 1.5, hasTp: false, template: 'trouser_front',
    guides: [
      { y: H, label: 'WAIST LINE', color: '#7c3aed' },
      { y: IS, label: 'HIP / SEAT LINE', color: '#dc2626' },
      { y: IS * 0.38, label: 'KNEE LINE', color: '#f59e0b' },
      { y: 0, label: 'HEM LINE', color: '#64748b' },
      { y: H - FR * 0.5, label: 'CROTCH LEVEL', color: '#059669' },
    ]
  };
};

const shapeTrouserBack = (m) => {
  const IS  = mGet(m,'inseam') || 81;
  const BR  = mGet(m,'backRise','rise') || 36;
  const HW  = mGet(m,'halfWaist','waist') || 40;
  const HH  = mGet(m,'halfHip','hip') || 50;
  const HT  = mGet(m,'halfThigh','thigh') || 32;
  const HK  = mGet(m,'halfKnee','knee') || 21;
  const HHm = mGet(m,'halfHem','hem') || 21.5;
  const H   = BR + IS;
  const hipY = IS;
  const kY  = IS * 0.38;
  const backWaistW = HW * 0.88; // back waist is narrower (CB seam angled for seat)

  // Back crotch: deeper and wider than front
  const crotch = [
    ...qbez([-backWaistW * 0.1, H], [-BR * 0.15, H - BR * 0.1], [-BR * 0.42, H - BR * 0.28], 4),
    ...qbez([-BR * 0.42, H - BR * 0.28], [-BR * 0.6, H - BR * 0.52], [-BR * 0.48, H - BR * 0.75], 5),
    ...qbez([-BR * 0.48, H - BR * 0.75], [-BR * 0.3, H - BR * 0.9], [-HT * 0.72, IS], 3),
  ];
  const sideRight = qbez([HHm, 0], [HH * 0.97, kY * 1.1], [HH, hipY], 5);
  const inseamLeft = qbez([-HT * 0.72, IS], [-HK * 0.98, kY], [-HHm, 0], 5);

  const pts = [
    [HHm, 0],
    ...sideRight,
    [HH, hipY],
    ...qbez([HH, hipY], [HW, H - 3], [HW * 0.9, H - 0.5], 4),
    [HW * 0.9, H - 0.5],
    [-backWaistW * 0.1, H],      // CB waist (angled, back waist slopes up to CB)
    ...crotch,
    ...inseamLeft,
    [-HHm, 0],
  ];
  return { pts, W: HH * 1.02, H: H + 0.5, hasTp: false, template: 'trouser_back',
    guides: [
      { y: H, label: 'WAIST LINE', color: '#7c3aed' },
      { y: IS, label: 'HIP / SEAT LINE', color: '#dc2626' },
      { y: IS * 0.38, label: 'KNEE LINE', color: '#f59e0b' },
      { y: 0, label: 'HEM LINE', color: '#64748b' },
    ]
  };
};

const shapeCollar = (m) => {
  const W = (mGet(m,'width','collarLength') || 20); // half collar
  const H = mGet(m,'height','collarHeight') || 6.5;
  // Collar has a slight curve on the neckline edge (bottom)
  const pts = [
    ...qbez([-W, 0], [0, -H * 0.12], [W, 0], 8),  // neckline edge (slight concave)
    ...qbez([W, 0], [W + 0.5, H * 0.5], [W * 0.95, H], 4), // outer right
    [W * 0.95, H],
    ...qbez([W * 0.95, H], [0, H + H * 0.06], [-W * 0.95, H], 6), // outer collar edge
    [-W * 0.95, H],
    ...qbez([-W * 0.95, H], [-W - 0.5, H * 0.5], [-W, 0], 4),
  ];
  return { pts, W: W + 1, H, hasTp: false, template: 'collar', guides: [] };
};

const shapeCollarStand = (m) => {
  const W = mGet(m,'width','collarLength') || 19.5;
  const H = mGet(m,'height','collarHeight','collarStandHeight') || 3.5;
  // Neck band: straight rectangle with curved neckline
  const pts = [
    ...qbez([-W, 0], [0, -H * 0.08], [W, 0], 6), // neckline curve
    [W, H], [-W, H],
  ];
  return { pts, W, H, hasTp: false, template: 'collar_stand', guides: [] };
};

const shapeCuff = (m) => {
  const W = (mGet(m,'width','cuffLength') || 24); // half cuff
  const H = mGet(m,'height','cuffHeight') || 7;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'cuff', guides: [] };
};

const shapeYoke = (m) => {
  const W = (mGet(m,'width','yokeWidth') || 45) / 2;
  const H = mGet(m,'height','yokeHeight') || 7;
  // Yoke: trapezoid, slightly narrower at neck edge
  const neckW = W * 0.78;
  const pts = [[-W, 0], [W, 0],
    ...qbez([W, 0], [neckW * 1.02, H * 0.5], [neckW, H], 4),
    ...qbez([neckW, H], [0, H + H * 0.05], [-neckW, H], 4),
    ...qbez([-neckW, H], [-neckW * 1.02, H * 0.5], [-W, 0], 4)
  ];
  return { pts, W, H, hasTp: false, template: 'yoke', guides: [] };
};

const shapePlacket = (m) => {
  const W = (mGet(m,'width','placketWidth') || 5) / 2;
  const H = mGet(m,'height','placketLength') || 21;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'placket', guides: [] };
};

const shapeRib = (m) => {
  const W = mGet(m,'width') || 17;
  const H = mGet(m,'height') || 2.5;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'rib', guides: [] };
};

const shapeWaistband = (m) => {
  const W = mGet(m,'width','halfWaist','waist') || 40;
  const H = mGet(m,'height','waistbandHeight') || 8;
  const pts = [[-W, 0], [W, 0], [W, H], [-W, H]];
  return { pts, W, H, hasTp: false, template: 'waistband', guides: [] };
};

const shapePocket = (m) => {
  const W = (mGet(m,'width') || 16);
  const H = mGet(m,'height') || 19;
  const pts = [[-W/2, 0], [W/2, 0], [W/2, H], [-W/2, H]];
  return { pts, W: W/2, H, hasTp: false, template: 'pocket', guides: [] };
};

const SHAPE_FNS = {
  body_front:     shapeBodyFront,
  body_back:      shapeBodyBack,
  sleeve:         shapeSleeve,
  collar:         shapeCollar,
  collar_stand:   shapeCollarStand,
  collar_polo:    shapeCollar,
  cuff:           shapeCuff,
  yoke:           shapeYoke,
  placket:        shapePlacket,
  rib:            shapeRib,
  waistband:      shapeWaistband,
  trouser_front:  shapeTrouserFront,
  trouser_back:   shapeTrouserBack,
  pocket:         shapePocket,
};


// ── Auto-place semantic anchor points on any shape ────────────────────────────
// For standard shapes: uses known mathematical positions.
// For traced shapes: scales template positions proportionally.
const autoPlaceAnchors = (template, W, H, m) => {
  const fn = TEMPLATE_ANCHORS?.[template];
  if (!fn) return [];
  const AD  = mGet(m,'armholeDepth') || Math.min(H*0.32, 24);
  const IS  = mGet(m,'inseam') || H * 0.74;
  const FR  = mGet(m,'frontRise','backRise','rise') || H * 0.26;
  const capH = W * 0.55;
  try {
    const anchors = fn(m, W, H, AD, IS, FR, capH);
    // Merge with any manually saved anchor overrides from the pattern
    return anchors;
  } catch { return []; }
};

// Module-level cache of shape templates saved via "Save as Standard" in the
// Reshape tool (category: 'shape_template'). Populated once by
// loadSavedShapeTemplates() below; read synchronously by buildDisplayPoly so
// every part of the app (thumbnails, PatternEditor, gallery) benefits once
// someone has hand-corrected a piece and saved it as the new default.
let SAVED_SHAPE_TEMPLATES = {};
export const loadSavedShapeTemplates = async () => {
  try {
    const res = await fetch(`${LOAD_URL}?category=shape_template`);
    const j = await res.json();
    const list = j?.data || j?.shapeTemplates || [];
    const map = {};
    (Array.isArray(list) ? list : []).forEach(t => {
      if (t?.templateKey && t?.tracedPoints?.length >= 3) map[t.templateKey] = t;
    });
    SAVED_SHAPE_TEMPLATES = map;
    return map;
  } catch (_) { return SAVED_SHAPE_TEMPLATES; }
};

// Convert a raw tracedPoints array (possibly with smooth/handle metadata)
// into { pts:[[x,y]...], ptsMeta:[{smooth,hIn,hOut}...] } centred the same
// way as the rest of buildDisplayPoly (x=0 at mid, y=0 at bottom).
const tracedToDisplay = (tp) => {
  const xs = tp.map(p => p.x), ys = tp.map(p => p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const maxX = Math.max(...xs), maxY = Math.max(...ys);
  const W = (maxX - minX) / 2, H = maxY - minY;
  const pts = tp.map(p => [p.x - minX - W, p.y - minY]);
  const ptsMeta = tp.map(p => ({
    smooth: !!p.smooth,
    hIn:  p.hIn  ? [p.hIn[0],  p.hIn[1]]  : null,
    hOut: p.hOut ? [p.hOut[0], p.hOut[1]] : null,
  }));
  return { pts, ptsMeta, W, H };
};

const buildDisplayPoly = (pattern) => {
  const tpl = detectTemplate(pattern);
  // 1. Prefer traced points (real digitised shape — includes anything
  //    hand-edited or inserted via the Reshape tool's node/curve editor)
  const tp = pattern.tracedPoints;
  if (tp && tp.length >= 3) {
    const { pts, ptsMeta, W, H } = tracedToDisplay(tp);
    const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
    return { pts, ptsMeta, W, H, hasTp: true, template: tpl, guides: [], anchors };
  }

  // 1b. Fall back to a shape someone previously hand-corrected and saved
  //     as the new standard for this piece type ("Save as Standard").
  const saved = SAVED_SHAPE_TEMPLATES[tpl];
  if (saved?.tracedPoints?.length >= 3) {
    const { pts, ptsMeta, W, H } = tracedToDisplay(saved.tracedPoints);
    const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
    return { pts, ptsMeta, W, H, hasTp: false, fromSavedTemplate: true, template: tpl, guides: [], anchors };
  }

  // 2. ── Use ShapeBuilder's own build() when template is a ShapeBuilder key ──
  // This is the most accurate path: uses the exact same math SpecDrivenShape uses,
  // so measurements stored by PartDNA's Spec Builder tab render correctly here.
  const sbTpl = GARMENT_TEMPLATES?.[tpl];
  if (sbTpl && typeof sbTpl.build === 'function') {
    try {
      const m = pattern.measurements || {};
      // Fill any missing keys with ShapeBuilder defaults so build() never gets NaN
      const filled = {};
      Object.entries(sbTpl.measurements).forEach(([k, def]) => {
        filled[k] = m[k] ?? def.default;
      });
      const shape = sbTpl.build(filled);
      // shape.polyline is [[x,y], ...] in centred y-up coords — same as our format
      const xs = shape.polyline.map(p => p[0]), ys = shape.polyline.map(p => p[1]);
      const minX = Math.min(...xs), maxX = Math.max(...xs);
      const minY = Math.min(...ys), H = Math.max(...ys) - minY;
      const W = (maxX - minX) / 2;
      // Normalise to centred (x=0 at mid, y=0 at bottom)
      const pts = shape.polyline.map(([x, y]) => [x, y - minY]);
      // Build construction guides from ShapeBuilder dims
      const guides = (shape.dims || []).filter(d => d.kind === 'v').map(d => ({
        y: d.y1, label: d.label || '', color: '#94a3b8',
      }));
      const anchors = autoPlaceAnchors(tpl, W, H, pattern.measurements || {});
      return { pts, ptsMeta: null, W, H, hasTp: false, template: tpl, guides, anchors };
    } catch (_) { /* fall through to manual shape fns */ }
  }

  // 3. Use MarkerMaker's own anatomical shape functions
  const shapeFn = SHAPE_FNS[tpl];
  if (shapeFn) {
    const result = shapeFn(pattern.measurements || {});
    const anchors = autoPlaceAnchors(tpl, result.W, result.H, pattern.measurements || {});
    return { ...result, ptsMeta: null, hasTp: false, anchors };
  }
  // 4. Generic fallback
  const m = pattern.measurements || {};
  const W = (mGet(m,'width','chestWidth') || 40) / 2;
  const H = (mGet(m,'height','bodyLength','length') || 60);
  return { pts:[[-W,0],[W,0],[W,H],[-W,H]], ptsMeta: null, W, H, hasTp:false, template:'generic', guides:[], anchors:[] };
};

// ── Build an SVG path "d" string from centred pts + optional curve metadata.
// meta[i] = { smooth, hIn:[dx,dy]|null, hOut:[dx,dy]|null } in cm, relative
// to pts[i]. A segment i→i+1 is drawn as a cubic bezier if either endpoint
// has a handle on that side; otherwise a straight line — so ordinary
// (all-corner) shapes render byte-for-byte identical to the old <polygon>.
const buildPathD = (pts, meta, txFn, tyFn) => {
  if (!pts || pts.length < 2) return '';
  const S = (i) => [txFn(pts[i][0]), tyFn(pts[i][1])];
  let d = `M ${S(0)[0].toFixed(1)} ${S(0)[1].toFixed(1)} `;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const mi = meta?.[i], mj = meta?.[j];
    const hOut = mi?.smooth && mi?.hOut ? mi.hOut : null;
    const hIn  = mj?.smooth && mj?.hIn  ? mj.hIn  : null;
    if (hOut || hIn) {
      const [ax, ay] = pts[i], [bx, by] = pts[j];
      const c1x = ax + (hOut ? hOut[0] : (bx - ax) * 0.33);
      const c1y = ay + (hOut ? hOut[1] : (by - ay) * 0.33);
      const c2x = bx + (hIn  ? hIn[0]  : (ax - bx) * 0.33);
      const c2y = by + (hIn  ? hIn[1]  : (ay - by) * 0.33);
      const p1 = [txFn(c1x), tyFn(c1y)], p2 = [txFn(c2x), tyFn(c2y)], pb = S(j);
      d += `C ${p1[0].toFixed(1)} ${p1[1].toFixed(1)} ${p2[0].toFixed(1)} ${p2[1].toFixed(1)} ${pb[0].toFixed(1)} ${pb[1].toFixed(1)} `;
    } else {
      const pb = S(j);
      d += `L ${pb[0].toFixed(1)} ${pb[1].toFixed(1)} `;
    }
  }
  return d + 'Z';
};

// Convert whatever is currently on screen (generated or saved-template pts)
// into a real tracedPoints array, "locking in" the shape so it can be
// hand-edited node-by-node from here on. Coordinates are shifted back from
// the centred display space (x=0 mid) to absolute (x starts at 0).
const displayPtsToTraced = (pts, W) =>
  pts.map(([x, y]) => ({ x: +(x + W).toFixed(2), y: +y.toFixed(2), smooth: false, hIn: null, hOut: null }));

// Nudge nodes near `idx` by (dxCm,dyCm) with a cosine falloff over `radius`
// index-steps in either direction (circular). Used by the Reshape tool's
// "Proportional" drag so stretching one point (e.g. the knee) smoothly
// carries the curve around it instead of putting a kink in the outline.
const applyProportionalNudge = (tracedPoints, idx, dxCm, dyCm, radius) => {
  const n = tracedPoints.length;
  if (!radius || radius <= 0) return tracedPoints;
  return tracedPoints.map((p, i) => {
    if (i === idx) return p;
    let dist = Math.abs(i - idx);
    dist = Math.min(dist, n - dist); // circular distance
    if (dist > radius) return p;
    const w = 0.5 * (1 + Math.cos((dist / radius) * Math.PI)); // 1 at idx → 0 at radius
    return { ...p, x: +(p.x + dxCm * w).toFixed(2), y: +(p.y + dyCm * w).toFixed(2) };
  });
};

// Find nearest point on polygon boundary to click (SVG px).
// pts is in cm; tx/ty convert cm→SVG px. Returns { edgeIdx, t, snapX, snapY }.
const nearestEdge = (pts, cx, cy, txFn, tyFn) => {
  let bestEdge = 0, bestT = 0, bestDist = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    const ax = txFn(pts[i][0]), ay = tyFn(pts[i][1]);
    const bx = txFn(pts[j][0]), by = tyFn(pts[j][1]);
    const dx = bx - ax, dy = by - ay, len2 = dx*dx + dy*dy;
    if (len2 < 0.001) continue;
    let t = ((cx-ax)*dx + (cy-ay)*dy) / len2;
    t = Math.max(0.01, Math.min(0.99, t));
    const nx = ax+t*dx, ny = ay+t*dy;
    const d = Math.hypot(cx-nx, cy-ny);
    if (d < bestDist) { bestDist = d; bestEdge = i; bestT = +t.toFixed(4); }
  }
  const [ax,ay] = pts[bestEdge], [bx,by] = pts[(bestEdge+1)%pts.length];
  const snapX = txFn(ax + (bx-ax)*bestT);
  const snapY = tyFn(ay + (by-ay)*bestT);
  return { edgeIdx: bestEdge, t: bestT, snapX, snapY };
};

// ── PatternSvgInline ────────────────────────────────────────────────────────
// Rendered inside a <svg> element with viewBox set by PatternEditor.
// SCALE and OX/OY must match what PatternEditor uses for the same pattern.
const PatternSvgInline = ({ pattern, showSA, measurePts, measureResult, SCALE, OX, OY }) => {
  const SC  = SCALE || 3.5;
  const { pts, ptsMeta, W, H, guides = [] } = buildDisplayPoly(pattern);
  const tx = x => (OX||0) + x * SC;
  const ty = y => (OY||0) + (H + 10) * SC - y * SC;
  const outlineD = buildPathD(pts, ptsMeta, tx, ty);
  const fSz = Math.max(6, Math.min(11, SC * 1.1));

  // SA: offset every polygon edge outward by sa (uniform parallel offset)
  const saVal = Math.max(...Object.values(pattern.seamAllowances || {top:1,right:1,bottom:1,left:1}));
  // Simple polygon offset: move each vertex outward along the angle bisector
  const saOffsetPts = pts.map((p, i) => {
    const prev = pts[(i - 1 + pts.length) % pts.length];
    const next = pts[(i + 1) % pts.length];
    const e1x = p[0]-prev[0], e1y = p[1]-prev[1];
    const e2x = next[0]-p[0], e2y = next[1]-p[1];
    const l1 = Math.hypot(e1x,e1y)||1, l2 = Math.hypot(e2x,e2y)||1;
    const n1x = -e1y/l1, n1y =  e1x/l1;
    const n2x = -e2y/l2, n2y =  e2x/l2;
    const nx = (n1x+n2x)/2, ny = (n1y+n2y)/2;
    const nl = Math.hypot(nx,ny)||1;
    return [p[0]+nx/nl*saVal, p[1]+ny/nl*saVal];
  });
  const saStr = saOffsetPts.map(p => `${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ');

  const grainX  = tx(0);
  const grainY1 = ty(H * 0.15);
  const grainY2 = ty(H * 0.85);
  // Dimension guides (width + height arrows on the pattern)
  const dimY    = ty(-1.5);          // width guide below pattern
  const dimX    = tx(W + 1.5);       // height guide right of pattern
  // ── 1cm background grid ──────────────────────────────────────────────────
  const gridLines = [];
  const gSpan = Math.ceil(W) + 4;
  for (let gx = -gSpan; gx <= gSpan; gx++) {
    const isMaj = gx % 5 === 0;
    gridLines.push(<line key={`gx${gx}`} x1={tx(gx)} y1={ty(-2)} x2={tx(gx)} y2={ty(H+8)}
      stroke={isMaj ? '#d1d5db' : '#f1f5f9'} strokeWidth={isMaj ? 0.5 : 0.3} />);
  }
  for (let gy = -2; gy <= H + 8; gy++) {
    const isMaj = gy % 5 === 0;
    gridLines.push(<line key={`gy${gy}`} x1={tx(-gSpan)} y1={ty(gy)} x2={tx(gSpan)} y2={ty(gy)}
      stroke={isMaj ? '#d1d5db' : '#f1f5f9'} strokeWidth={isMaj ? 0.5 : 0.3} />);
  }

  return (
    <g>
      {/* Grid */}
      {gridLines}
      {/* Construction guide lines from shape generator */}
      {guides.map((g, i) => (
        <g key={`guide${i}`}>
          <line x1={tx(-W - 2)} y1={ty(g.y)} x2={tx(W + 2)} y2={ty(g.y)}
            stroke={g.color} strokeWidth={Math.max(0.5, SC * 0.12)}
            strokeDasharray={`${SC * 0.8},${SC * 0.5}`} opacity="0.7"/>
          <text x={tx(-W - 1.5)} y={ty(g.y) - 2} fontSize={fSz * 0.75}
            fill={g.color} fontWeight="bold" opacity="0.85">{g.label}</text>
        </g>
      ))}
      {/* SA outline follows actual polygon shape */}
      {showSA && <polygon points={saStr} fill="none" stroke="#dc2626" strokeWidth={Math.max(0.5,SC*0.15)} strokeDasharray={`${SC*1.2},${SC*0.8}`} />}
      {/* Pattern outline — path supports smooth (bezier) nodes from the Reshape tool;
          renders identically to the old straight-line polygon when no node is smooth */}
      <path d={outlineD} fill="rgba(59,130,246,0.07)" stroke="#1e293b" strokeWidth={Math.max(1,SC*0.3)} strokeLinejoin="round" />
      {/* Grain line */}
      <line x1={grainX} y1={grainY1} x2={grainX} y2={grainY2} stroke="#059669" strokeWidth={Math.max(0.6,SC*0.2)} strokeDasharray={`${SC},${SC*0.6}`} />
      <text x={grainX + fSz*0.5} y={(grainY1+grainY2)/2} fontSize={fSz*0.8} fill="#059669">GRAIN</text>

      {/* ── Semantic anchor points — named anatomical measurement markers ── */}
      {(pattern.anchors || []).map(a => {
        const ax = tx(a.x), ay = ty(a.y);
        // Anchor: 0.25cm radius in pattern space — always a small crisp dot
        const r  = Math.max(2, SC * 0.25);
        const arm = SC * 0.45;  // crosshair arm = 0.45 cm in pattern space
        return (
          <g key={a.id}>
            {/* Crosshair arms */}
            <line x1={ax-arm} y1={ay} x2={ax+arm} y2={ay} stroke={a.color||'#ef4444'} strokeWidth={Math.max(0.6,SC*0.14)} opacity="0.85"/>
            <line x1={ax} y1={ay-arm} x2={ax} y2={ay+arm} stroke={a.color||'#ef4444'} strokeWidth={Math.max(0.6,SC*0.14)} opacity="0.85"/>
            {/* Dot */}
            <circle cx={ax} cy={ay} r={r} fill={a.color||'#ef4444'} opacity="0.95"/>
            {/* Label */}
            <rect x={ax+r*1.2} y={ay-fSz*0.6} width={fSz*0.7*a.label.length*0.55+6} height={fSz*0.9} rx="2"
              fill="rgba(255,255,255,0.88)" />
            <text x={ax+r*1.5} y={ay+fSz*0.25} fontSize={fSz*0.72} fill={a.color||'#ef4444'} fontWeight="bold">
              {a.label}
            </text>
          </g>
        );
      })}
      {/* ── Dimension guides ── */}
      {/* Width arrow */}
      <line x1={tx(-W)} y1={dimY} x2={tx(W)} y2={dimY} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={tx(-W)} y1={dimY-fSz*0.5} x2={tx(-W)} y2={dimY+fSz*0.5} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={tx( W)} y1={dimY-fSz*0.5} x2={tx( W)} y2={dimY+fSz*0.5} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <text x={tx(0)} y={dimY + fSz*1.3} textAnchor="middle" fontSize={fSz} fill="#475569" fontWeight="bold">{(W*2).toFixed(1)} cm</text>
      {/* Height arrow */}
      <line x1={dimX} y1={ty(0)} x2={dimX} y2={ty(H)} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={dimX-fSz*0.5} y1={ty(0)} x2={dimX+fSz*0.5} y2={ty(0)} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <line x1={dimX-fSz*0.5} y1={ty(H)} x2={dimX+fSz*0.5} y2={ty(H)} stroke="#64748b" strokeWidth={Math.max(0.4,SC*0.12)}/>
      <text x={dimX + fSz*1.4} y={(ty(0)+ty(H))/2} textAnchor="middle" fontSize={fSz} fill="#475569" fontWeight="bold"
        transform={`rotate(-90 ${dimX + fSz*1.4} ${(ty(0)+ty(H))/2})`}>{H.toFixed(1)} cm</text>

      {/* Notches — edgeIdx + t on actual polygon */}
      {(pattern.notches || []).map((n, i) => {
        const ei_ = (n.edgeIdx ?? 0) % pts.length;
        const [ax,ay] = pts[ei_], [bx,by] = pts[(ei_+1)%pts.length];
        const t = n.t ?? 0.5;
        const npx = tx(ax+(bx-ax)*t), npy = ty(ay+(by-ay)*t);
        return (
          <g key={n.id||i}>
            {/* Industry-standard chevron notch pointing outward from the seam edge */}
            {(() => {
              const ei_ = (n.edgeIdx ?? 0) % pts.length;
              const [ax_,ay_] = pts[ei_], [bx_,by_] = pts[(ei_+1)%pts.length];
              // Edge tangent in cm space
              const eDx = bx_-ax_, eDy = by_-ay_;
              const eLen = Math.hypot(eDx,eDy)||1;
              // Left normal (CCW y-up polygon): (-eDy, eDx)/len
              const lnx = -eDy/eLen, lny = eDx/eLen;
              // Outward = points away from pattern centroid (~0, H/2)
              const mx = ax_+(bx_-ax_)*(n.t??0.5), my = ay_+(by_-ay_)*(n.t??0.5);
              const dot = lnx*(0-mx) + lny*(H/2-my);
              const onx = dot>0 ? -lnx : lnx;
              const ony = dot>0 ? -lny : lny;
              // Convert outward normal to SVG space (y flipped: ony → -ony in SVG)
              const snx = onx*SC, sny = -ony*SC;
              const snLen = Math.hypot(snx,sny)||1;
              const depth = SC*0.55, arm = SC*0.3;
              // Tip of chevron (outward)
              const tx_ = npx + snx/snLen*depth, ty_ = npy + sny/snLen*depth;
              // Arms (perpendicular to normal)
              const px_ = -sny/snLen, py_ = snx/snLen;
              const sw = Math.max(1, SC*0.18);
              if (n.type === 'mark') {
                // Free mark = diamond ◇
                return <>
                  <line x1={npx-SC*0.25} y1={npy} x2={npx} y2={npy-SC*0.25} stroke="#f59e0b" strokeWidth={sw}/>
                  <line x1={npx} y1={npy-SC*0.25} x2={npx+SC*0.25} y2={npy} stroke="#f59e0b" strokeWidth={sw}/>
                  <line x1={npx+SC*0.25} y1={npy} x2={npx} y2={npy+SC*0.25} stroke="#f59e0b" strokeWidth={sw}/>
                  <line x1={npx} y1={npy+SC*0.25} x2={npx-SC*0.25} y2={npy} stroke="#f59e0b" strokeWidth={sw}/>
                </>;
              }
              return <>
                <line x1={npx+px_*arm} y1={npy+py_*arm} x2={tx_} y2={ty_} stroke="#f59e0b" strokeWidth={sw*1.4} strokeLinecap="round"/>
                <line x1={tx_} y1={ty_} x2={npx-px_*arm} y2={npy-py_*arm} stroke="#f59e0b" strokeWidth={sw*1.4} strokeLinecap="round"/>
                {/* Base line at seam edge */}
                <line x1={npx+px_*arm} y1={npy+py_*arm} x2={npx-px_*arm} y2={npy-py_*arm} stroke="#f59e0b" strokeWidth={sw*0.7} strokeLinecap="round"/>
              </>;
            })()}
          </g>
        );
      })}

      {/* Measurement lines — stored in cm space, converted via tx/ty */}
      {(pattern.measurementPoints || []).map(mp => {
        const ax=tx(mp.x1), ay=ty(mp.y1), bx=tx(mp.x2), by=ty(mp.y2);
        const midX=(ax+bx)/2, midY=(ay+by)/2;
        return (
          <g key={mp.id}>
            <line x1={ax} y1={ay} x2={bx} y2={by} stroke="#3b82f6" strokeWidth="1.5"/>
            <line x1={ax-5} y1={ay-5} x2={ax+5} y2={ay+5} stroke="#2563eb" strokeWidth="1.5"/>
            <line x1={bx-5} y1={by-5} x2={bx+5} y2={by+5} stroke="#2563eb" strokeWidth="1.5"/>
            <rect x={midX-28} y={midY-8} width={56} height={13} rx="3"
              fill="white" stroke="#3b82f6" strokeWidth="0.8" opacity="0.95"/>
            <text x={midX} y={midY+2} textAnchor="middle" fontSize={fSz*0.85} fill="#1d4ed8" fontWeight="bold">
              {mp.label}: {mp.dist?.toFixed(1)} cm
            </text>
          </g>
        );
      })}

      {/* Live drag measurement line */}
      {measurePts?.liveLine && (
        <g>
          <line x1={measurePts.liveLine.from.x} y1={measurePts.liveLine.from.y}
                x2={measurePts.liveLine.to.x}   y2={measurePts.liveLine.to.y}
            stroke="#0ea5e9" strokeWidth={Math.max(1,SC*0.2)} strokeDasharray={`${SC*1.2},${SC*0.7}`} opacity="0.8"/>
          <text x={(measurePts.liveLine.from.x+measurePts.liveLine.to.x)/2}
                y={(measurePts.liveLine.from.y+measurePts.liveLine.to.y)/2-fSz*0.7}
            fontSize={fSz*0.9} fill="#0ea5e9" textAnchor="middle" fontWeight="bold">
            {measurePts.liveLine.dist} cm
          </text>
        </g>
      )}
      {measurePts?.confirmed && (
        <g>
          <line x1={measurePts.confirmed.from.x} y1={measurePts.confirmed.from.y}
                x2={measurePts.confirmed.to.x}   y2={measurePts.confirmed.to.y}
            stroke="#22c55e" strokeWidth={Math.max(1.2,SC*0.25)}/>
          <text x={(measurePts.confirmed.from.x+measurePts.confirmed.to.x)/2}
                y={(measurePts.confirmed.from.y+measurePts.confirmed.to.y)/2-fSz*0.7}
            fontSize={fSz*0.9} fill="#16a34a" textAnchor="middle" fontWeight="bold">
            ✓ {measurePts.confirmed.dist} cm
          </text>
        </g>
      )}
      <text x={tx(0)} y={ty(-4)} textAnchor="middle" fontSize={fSz*1.1} fill="#334155" fontWeight="bold">
        {pattern.partName}
      </text>
    </g>
  );
};

// ── PatternEditor ────────────────────────────────────────────────────────────
const PatternEditor = ({ patterns, setPatterns }) => {
  const [selIdx, setSelIdx]           = useState(0);
  const [tool, setTool]               = useState('select');
  const [showSA, setShowSA]           = useState(true);
  const [zoom, setZoom]               = useState(1);         // 0.5 – 4
  const [pan, setPan]                 = useState({x:0,y:0});
  const [isPanning, setIsPanning]     = useState(false);
  const [panStart, setPanStart]       = useState({x:0,y:0});
  const [snapLine, setSnapLine]       = useState(null);
  const [measureDrag, setMeasureDrag] = useState(null);  // {from:{x,y}} while dragging
  const [liveMeasure, setLiveMeasure] = useState(null);  // {from,to,dist} live preview
  const [measureResult, setMeasureResult] = useState(null); // completed, pending save
  const [hoverXY, setHoverXY]         = useState(null);
  const [editSpecKey, setEditSpecKey]   = useState('');
  const [editSpecVal, setEditSpecVal]   = useState('');
  const [showAnchors, setShowAnchors]   = useState(true);
  const [calcResults, setCalcResults]   = useState(null); // auto-calculated measurements
  // Undo history — each entry is a snapshot of patterns[]
  const [history, setHistory]           = useState([]);
  // ── Reshape tool: node/curve editor state ──────────────────────────────
  const [reshapeSel, setReshapeSel]     = useState(null);  // selected node index
  const [dragNode, setDragNode]         = useState(null);  // {idx, part:'point'|'hIn'|'hOut'}
  const [proportional, setProportional] = useState(false);
  const [propRadius, setPropRadius]     = useState(2);      // node-index falloff radius
  const [tplVersion, setTplVersion]     = useState(0);      // bumps when saved templates load
  const svgRef = useRef(null);
  const pat = patterns[selIdx];

  // Load any "Save as Standard" shape templates once, so pieces without their
  // own tracedPoints can use a hand-corrected shape instead of the generic one.
  useEffect(() => { loadSavedShapeTemplates().then(() => setTplVersion(v => v + 1)); }, []);

  // Push current patterns to undo stack
  const pushHistory = useCallback(() => {
    setHistory(h => [...h.slice(-29), patterns.map(p => ({...p}))]);
  }, [patterns]);

  // Cmd+Z / Ctrl+Z undo handler
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        setHistory(h => {
          if (!h.length) return h;
          const prev = h[h.length - 1];
          setPatterns(prev);
          return h.slice(0, -1);
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setPatterns]);

  // ── Display geometry ────────────────────────────────────────────────────
  const { pts, ptsMeta, W, H, hasTp, fromSavedTemplate } = useMemo(
    () => pat ? buildDisplayPoly(pat) : { pts:[], ptsMeta:null, W:20, H:60, hasTp:false, fromSavedTemplate:false },
    [JSON.stringify(pat?.measurements), JSON.stringify(pat?.tracedPoints), tplVersion]
  );

  // BASE_SC: scale that fits the pattern in 80% of an 800×500 canvas at zoom=1
  const BASE_SC = useMemo(() => {
    if (!W || !H) return 3.5;
    const fitX = (800 * 0.80) / (W * 2);
    const fitY = (500 * 0.80) / H;
    return Math.max(1, Math.min(fitX, fitY, 12));
  }, [W, H]);

  const SC   = BASE_SC * zoom;
  const PAD  = 50;  // canvas padding in SVG user units
  const OX   = PAD + W * SC + pan.x;
  const OY   = PAD + pan.y;
  const txFn = x => OX + x * SC;
  const tyFn = y => OY + (H + 5) * SC - y * SC;

  // ViewBox sized to actual content — no min constraint
  // This ensures text scales proportionally with the pattern
  const VW = W * 2 * SC + PAD * 3;
  const VH = (H + 10) * SC + PAD * 2;

  // Auto-fit when pattern changes
  useEffect(() => { setZoom(1); setPan({x:0,y:0}); setReshapeSel(null); setDragNode(null); }, [selIdx]);

  // ── Reshape tool helpers ────────────────────────────────────────────────
  // Turn whatever is currently displayed (generated shape or saved template)
  // into a real, per-node-editable tracedPoints array on this pattern.
  const ensureTraced = () => {
    if (hasTp) return pat.tracedPoints;
    const traced = displayPtsToTraced(pts, W);
    setPatterns(prev => prev.map((p,i) => i===selIdx ? {...p, tracedPoints: traced, updatedOn:new Date().toISOString()} : p));
    return traced;
  };

  // NOTE: PatternSvgInline draws with ty = OY + (H+10)*SC − y*SC (see its own
  // tx/ty), which differs from this component's txFn/tyFn (H+5) used by the
  // notch/measure tools. To keep reshape nodes sitting exactly on the drawn
  // outline we mirror PatternSvgInline's transform here rather than reuse
  // txFn/tyFn.
  const txR = x => OX + x * SC;
  const tyR = y => OY + (H + 10) * SC - y * SC;
  const toCmR = (sx, sy) => ({ x: (sx - OX) / SC, y: (OY + (H + 10) * SC - sy) / SC });

  const nodeScreenPos = (i) => [txR(pts[i][0]), tyR(pts[i][1])];
  const handleScreenPos = (i, side) => {
    const m = ptsMeta?.[i]; const h = side === 'hOut' ? m?.hOut : m?.hIn;
    if (!m?.smooth || !h) return null;
    return [txR(pts[i][0] + h[0]), tyR(pts[i][1] + h[1])];
  };
  const HIT_R = 9; // px

  const hitTestReshape = (sx, sy) => {
    // Handles first (smaller targets, sit near their node)
    for (let i = 0; i < pts.length; i++) {
      for (const side of ['hOut','hIn']) {
        const hp = handleScreenPos(i, side);
        if (hp && Math.hypot(hp[0]-sx, hp[1]-sy) <= HIT_R) return { idx:i, part:side };
      }
    }
    for (let i = 0; i < pts.length; i++) {
      const [nx,ny] = nodeScreenPos(i);
      if (Math.hypot(nx-sx, ny-sy) <= HIT_R) return { idx:i, part:'point' };
    }
    return null;
  };

  const getSvgPt = (e) => {
    const svg = svgRef.current; if (!svg) return {x:0,y:0};
    // getScreenCTM() is the correct W3C method — handles CSS transforms,
    // aspect-ratio differences, devicePixelRatio, and browser zoom correctly.
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return {x:0,y:0};
    const sp = pt.matrixTransform(ctm.inverse());
    return { x: +sp.x.toFixed(2), y: +sp.y.toFixed(2) };
  };

  // Convert SVG-canvas coordinates to pattern cm space
  const toCm = (sx, sy) => ({
    x: +((sx - OX) / SC).toFixed(3),
    y: +((OY + (H + 5) * SC - sy) / SC).toFixed(3),
  });

  const onWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.max(0.4, Math.min(4, z + (e.deltaY < 0 ? 0.15 : -0.15))));
  };

  const onMouseDown = (e) => {
    if (e.button === 1 || (e.button === 0 && tool === 'select')) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.preventDefault();
      return;
    }
    if (e.button === 0 && tool === 'measure') {
      const pt = getSvgPt(e);
      setMeasureDrag({ from: pt });
      setLiveMeasure(null);
      setMeasureResult(null);
      e.preventDefault();
    }
    if (e.button === 0 && tool === 'reshape') {
      const svgPt = getSvgPt(e);
      const hit = hitTestReshape(svgPt.x, svgPt.y);
      if (hit) {
        ensureTraced(); // lock in a real, editable shape the moment you actually start dragging a node
        pushHistory();
        setReshapeSel(hit.idx);
        setDragNode({ ...hit, lastCm: toCmR(svgPt.x, svgPt.y) });
      } else {
        setReshapeSel(null);
      }
      e.preventDefault();
    }
  };

  const onMouseMove = (e) => {
    const {x,y} = getSvgPt(e);
    setHoverXY({x,y});
    if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
      return;
    }
    if (tool === 'notch' && pts.length >= 3) {
      setSnapLine(nearestEdge(pts, x, y, txFn, tyFn));
    } else { setSnapLine(null); }
    // Live measurement drag
    if (tool === 'measure' && measureDrag) {
      const pxDist = Math.hypot(x - measureDrag.from.x, y - measureDrag.from.y);
      const distCm = +(pxDist / SC).toFixed(2);
      setLiveMeasure({ from: measureDrag.from, to: {x,y}, dist: distCm });
    }
    // Reshape tool: drag the selected node or curve handle
    if (tool === 'reshape' && dragNode) {
      const cm = toCmR(x, y);
      const dxCm = cm.x - dragNode.lastCm.x, dyCm = cm.y - dragNode.lastCm.y;
      if (Math.abs(dxCm) < 0.001 && Math.abs(dyCm) < 0.001) return;
      setPatterns(prev => prev.map((p,pi) => {
        if (pi !== selIdx) return p;
        let tp = [...(p.tracedPoints || [])];
        const node = { ...tp[dragNode.idx] };
        if (dragNode.part === 'point') {
          node.x = +(node.x + dxCm).toFixed(2);
          node.y = +(node.y + dyCm).toFixed(2);
          tp[dragNode.idx] = node;
          if (proportional) tp = applyProportionalNudge(tp, dragNode.idx, dxCm, dyCm, propRadius);
        } else {
          // Dragging a bezier handle: move that handle only (relative offset from the node)
          const key = dragNode.part; // 'hIn' | 'hOut'
          const cur = node[key] || [0, 0];
          node[key] = [+(cur[0] + dxCm).toFixed(2), +(cur[1] + dyCm).toFixed(2)];
          node.smooth = true;
          tp[dragNode.idx] = node;
        }
        return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
      }));
      setDragNode(d => ({ ...d, lastCm: cm }));
    }
  };

  const onMouseUp = (e) => {
    setIsPanning(false);
    if (tool === 'measure' && measureDrag) {
      const pt = getSvgPt(e);
      const pxDist = Math.hypot(pt.x - measureDrag.from.x, pt.y - measureDrag.from.y);
      if (pxDist > 4) { // ignore accidental micro-drags
        const distCm = +(pxDist / SC).toFixed(2);
        setMeasureResult({ from: measureDrag.from, to: pt, dist: distCm });
      }
      setMeasureDrag(null);
      setLiveMeasure(null);
    }
    if (tool === 'reshape' && dragNode) setDragNode(null);
  };

  const handleSvgClick = (e) => {
    if (isPanning) return;
    const {x,y} = getSvgPt(e);
    if (tool === 'notch' && pts.length >= 3) {
      pushHistory();
      const snap = nearestEdge(pts, x, y, txFn, tyFn);
      const snapDist = Math.hypot(x - snap.snapX, y - snap.snapY);
      let newNotch;
      if (snapDist <= SC * 1.5) {
        // Within 1.5 cm of an edge → snap to edge, render as chevron >
        newNotch = { id:`n-${Date.now()}`, edgeIdx: snap.edgeIdx, t: snap.t, type:'slit', depth:1 };
      } else {
        // Far from edge → free interior mark (dart point, pocket, etc.)
        const cm = toCm(x, y);
        newNotch = { id:`n-${Date.now()}`, edgeIdx: 0, t: 0,
          // Store interior position as normalised fractions so it's zoom-invariant
          nx: +(cm.x / (W * 2) + 0.5).toFixed(4), ny: +(cm.y / H).toFixed(4),
          type:'mark', depth:0 };
      }
      setPatterns(prev => prev.map((p,i) =>
        i===selIdx ? {...p, notches:[...(p.notches||[]), newNotch], updatedOn:new Date().toISOString()} : p
      ));
      setSnapLine(null);
    }
    // Measure handled entirely by drag (mousedown/move/up)
  };

  // Double-click to save measurement as permanent annotation, or (in Reshape)
  // to insert a new node on the nearest edge.
  const handleDblClick = (e) => {
    if (tool === 'measure' && measureResult) {
      pushHistory();
      addMeasurementAnnotation();
      return;
    }
    if (tool === 'reshape' && pts.length >= 3) {
      const svgPt = getSvgPt(e);
      if (hitTestReshape(svgPt.x, svgPt.y)) return; // double-click landed on an existing node/handle
      const snap = nearestEdge(pts, svgPt.x, svgPt.y, txR, tyR);
      const snapDist = Math.hypot(svgPt.x - snap.snapX, svgPt.y - snap.snapY);
      if (snapDist > SC * 2) return; // too far from any edge
      pushHistory();
      const traced = ensureTraced();
      const [ax, ay] = pts[snap.edgeIdx], [bx, by] = pts[(snap.edgeIdx + 1) % pts.length];
      const newNode = {
        x: +(ax + (bx - ax) * snap.t + W).toFixed(2),
        y: +(ay + (by - ay) * snap.t).toFixed(2),
        smooth: false, hIn: null, hOut: null,
      };
      setPatterns(prev => prev.map((p,i) => {
        if (i !== selIdx) return p;
        const tp = [...(p.tracedPoints || traced)];
        tp.splice(snap.edgeIdx + 1, 0, newNode);
        return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
      }));
      setReshapeSel(snap.edgeIdx + 1);
    }
  };

  // Toggle the selected node between a sharp corner and a smooth (bezier) curve.
  // Smoothing derives symmetric handles from the two neighbouring nodes so the
  // curve starts tangent-continuous; drag the little handle squares to refine.
  const toggleNodeSmooth = () => {
    if (reshapeSel == null) return;
    pushHistory();
    const traced = ensureTraced();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const tp = [...(p.tracedPoints || traced)];
      const n = tp.length, idx = reshapeSel;
      const node = { ...tp[idx] };
      if (node.smooth) {
        node.smooth = false; node.hIn = null; node.hOut = null;
      } else {
        const prevP = tp[(idx - 1 + n) % n], nextP = tp[(idx + 1) % n];
        const dx = (nextP.x - prevP.x) * 0.18, dy = (nextP.y - prevP.y) * 0.18;
        node.smooth = true; node.hIn = [-dx, -dy]; node.hOut = [dx, dy];
      }
      tp[idx] = node;
      return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
    }));
  };

  const deleteNode = () => {
    if (reshapeSel == null) return;
    const traced = ensureTraced();
    if ((pat.tracedPoints || traced).length <= 3) { alert('A shape needs at least 3 points.'); return; }
    pushHistory();
    setPatterns(prev => prev.map((p,i) => {
      if (i !== selIdx) return p;
      const tp = (p.tracedPoints || traced).filter((_,ni) => ni !== reshapeSel);
      return { ...p, tracedPoints: tp, updatedOn: new Date().toISOString() };
    }));
    setReshapeSel(null);
  };

  const resetToGenerated = () => {
    if (!window.confirm('Discard hand-edited shape and revert to the generated/standard shape for this piece?')) return;
    pushHistory();
    setPatterns(prev => prev.map((p,i) => i===selIdx ? {...p, tracedPoints: null, updatedOn:new Date().toISOString()} : p));
    setReshapeSel(null);
  };

  // Persist the current piece's tracedPoints as the new default for this
  // piece type (template), so future "Load standard pieces" / new styles
  // start from this corrected shape instead of the generic placeholder.
  const [savingStandard, setSavingStandard] = useState(false);
  const saveAsStandard = async () => {
    const traced = ensureTraced();
    const tpKey = buildDisplayPoly({ ...pat, tracedPoints: pat.tracedPoints || traced }).template;
    if (!tpKey || tpKey === 'generic') { alert('Could not determine a piece type to save this shape under.'); return; }
    if (!window.confirm(`Save this shape as the new standard "${tpKey}"? It will be used for every future piece of this type.`)) return;
    setSavingStandard(true);
    try {
      const res = await fetch(SAVE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: 'shape_template',
          data: {
            id: 0,
            templateKey: tpKey,
            pieceName: pat.partName,
            tracedPoints: pat.tracedPoints || traced,
            seamAllowances: pat.seamAllowances,
            notches: pat.notches,
            savedOn: new Date().toISOString(),
          },
        }),
      });
      const j = await res.json().catch(() => null);
      if (j && j.success === false) throw new Error(j.error || 'Save failed');
      await loadSavedShapeTemplates();
      setTplVersion(v => v + 1);
      alert(`✓ Saved as the new standard shape for "${tpKey}".`);
    } catch (e) {
      alert('Could not save to the standard library: ' + e.message + '\n\n(This needs a "shape_template" category on save_universal.php / load_universal.php — check with your backend if this is the first time saving one.)');
    } finally {
      setSavingStandard(false);
    }
  };

  const addMeasurementAnnotation = () => {
    if (!measureResult) return;
    const p1 = toCm(measureResult.from.x, measureResult.from.y);
    const p2 = toCm(measureResult.to.x,   measureResult.to.y);
    const newMp = {
      id:    `mp-${Date.now()}`,
      label: `Meas ${(pat.measurementPoints||[]).length+1}`,
      x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y,
      dist: +Math.hypot(p2.x-p1.x, p2.y-p1.y).toFixed(2),
    };
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, measurementPoints:[...(p.measurementPoints||[]), newMp]} : p
    ));
    setMeasureResult(null);
  };

  const updateSA = (side, val) => {
    pushHistory();
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, seamAllowances:{...p.seamAllowances,[side]:parseFloat(val)||0}, updatedOn:new Date().toISOString()} : p
    ));
  };

  const addSpec = () => {
    if (!editSpecKey.trim()) return;
    setPatterns(prev => prev.map((p,i) =>
      i===selIdx ? {...p, measurements:{...p.measurements,[editSpecKey.trim()]:parseFloat(editSpecVal)||0}, updatedOn:new Date().toISOString()} : p
    ));
    setEditSpecKey(''); setEditSpecVal('');
  };

  const updateSpec = (key, val) => setPatterns(prev => prev.map((p,i) =>
    i===selIdx ? {...p, measurements:{...p.measurements,[key]:parseFloat(val)||0}, updatedOn:new Date().toISOString()} : p
  ));

  const scaleLabel = `1 cm = ${SC.toFixed(1)}px  |  ~1:${Math.round(28.35/SC)} scale`;

  if (!pat) return <div style={{padding:16,color:'#94a3b8'}}>No patterns loaded.</div>;

  return (
    <div style={{padding:16,display:'flex',gap:12,height:'100%',overflow:'hidden'}}>

      {/* Left: part list */}
      <div style={{width:140,flexShrink:0,overflowY:'auto'}}>
        <div style={{fontSize:10,fontWeight:'bold',color:'#64748b',textTransform:'uppercase',marginBottom:6}}>Parts</div>
        {patterns.map((p,i)=>(
          <button key={p.id} onClick={()=>{setSelIdx(i);setMeasureResult(null);setSnapLine(null);setReshapeSel(null);setDragNode(null);}}
            style={{width:'100%',textAlign:'left',padding:'6px 8px',marginBottom:4,borderRadius:6,
              border:i===selIdx?'1px solid #7c3aed':'1px solid #e2e8f0',
              background:i===selIdx?'#f5f3ff':'white',cursor:'pointer',fontSize:11,fontWeight:i===selIdx?'bold':'normal'}}>
            {p.partName}
            {p.tracedPoints?.length>=3 && <span style={{marginLeft:4,fontSize:8,color:'#059669'}}>●</span>}
          </button>
        ))}
      </div>

      {/* Centre: SVG canvas */}
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:6,minWidth:0}}>

        {/* Toolbar */}
        <div style={{display:'flex',gap:5,background:'#1e293b',padding:'5px 10px',borderRadius:8,flexWrap:'wrap',alignItems:'center'}}>
          {[
            {id:'select',  label:'↖ Pan',    color:'#6366f1'},
            {id:'notch',   label:'✂ Notch',  color:'#f59e0b'},
            {id:'measure', label:'📏 Measure',color:'#0ea5e9'},
            {id:'reshape', label:'✎ Reshape',color:'#a855f7'},
          ].map(t=>(
            <button key={t.id} onClick={()=>{setTool(t.id);setMeasureResult(null);setSnapLine(null);setReshapeSel(null);setDragNode(null);}}
              style={{padding:'4px 9px',fontSize:10,fontWeight:tool===t.id?'bold':'normal',
                background:tool===t.id?t.color:'rgba(255,255,255,0.08)',color:tool===t.id?'white':'#94a3b8',
                border:`1px solid ${tool===t.id?t.color:'rgba(255,255,255,0.1)'}`,borderRadius:5,cursor:'pointer'}}>
              {t.label}
            </button>
          ))}
          <div style={{marginLeft:4,display:'flex',gap:4}}>
            <button onClick={()=>setZoom(z=>Math.min(4,z+0.25))}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>+</button>
            <span style={{fontSize:9,color:'#94a3b8',alignSelf:'center'}}>{Math.round(zoom*100)}%</span>
            <button onClick={()=>setZoom(z=>Math.max(0.4,z-0.25))}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:12,fontWeight:'bold'}}>−</button>
            <button onClick={()=>{setZoom(1);setPan({x:0,y:0});}}
              style={{padding:'3px 7px',background:'rgba(255,255,255,0.1)',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:9}}>⊡ Reset</button>
          </div>
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#94a3b8',marginLeft:4}}>
            <input type="checkbox" checked={showSA} onChange={e=>setShowSA(e.target.checked)}/> SA
          </label>
          <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#f59e0b',marginLeft:2}}>
            <input type="checkbox" checked={showAnchors} onChange={e=>setShowAnchors(e.target.checked)}/> ⚓ Anchors
          </label>
          {/* Auto-calculate standard measurements from anchor positions */}
          <button onClick={() => {
            const poly = buildDisplayPoly(pat);
            const anchors = poly.anchors || [];
            const tpl = poly.template;
            const results = calcMeasurementsFromAnchors(anchors, tpl);
            if (Object.keys(results).length === 0) { alert('No measurement definitions available for this piece type.'); return; }
            setCalcResults(results);
            // Offer to push into spec builder
            if (window.confirm(`Calculated ${Object.keys(results).length} measurements from anchor points. Apply to Spec Builder?`)) {
              pushHistory();
              setPatterns(prev => prev.map((p,i) => {
                if (i !== selIdx) return p;
                const merged = { ...p.measurements };
                Object.entries(results).forEach(([k,v]) => {
                  // Only update if key looks like a spec key (no spaces)
                  if (!/\s/.test(k)) merged[k] = v;
                });
                return { ...p, measurements: merged, updatedOn: new Date().toISOString() };
              }));
            }
          }}
            style={{padding:'3px 9px',background:'rgba(245,158,11,0.15)',color:'#f59e0b',border:'1px solid rgba(245,158,11,0.3)',borderRadius:4,cursor:'pointer',fontSize:9,fontWeight:'bold',marginLeft:2}}>
            📐 Calc Measurements
          </button>
          {hoverXY && <span style={{marginLeft:'auto',fontSize:9,color:'#64748b',fontFamily:'monospace'}}>
            x:{hoverXY.x.toFixed(0)} y:{hoverXY.y.toFixed(0)}
          </span>}
          {/* Undo button */}
          <button onClick={()=>{ setHistory(h=>{ if(!h.length) return h; setPatterns(h[h.length-1]); return h.slice(0,-1); }); }}
            title="Undo (Cmd+Z / Ctrl+Z)" disabled={history.length===0}
            style={{padding:'3px 7px',background:history.length?'rgba(239,68,68,0.15)':'rgba(255,255,255,0.04)',color:history.length?'#f87171':'#475569',border:'none',borderRadius:4,cursor:history.length?'pointer':'not-allowed',fontSize:9,marginLeft:4}}>
            ↩ Undo ({history.length})
          </button>
          {tool==='notch' && <span style={{fontSize:9,color:'#fbbf24',marginLeft:4}}>Click near edge → ▶ chevron notch · Click inside pattern → ◇ reference mark</span>}
          {tool==='measure' && !measureDrag && !measureResult && <span style={{fontSize:9,color:'#38bdf8',marginLeft:4}}>Drag to measure · double-click to save annotation</span>}
          {tool==='measure' && measureDrag  && <span style={{fontSize:9,color:'#38bdf8',marginLeft:4}}>↔ {liveMeasure?.dist ?? '…'} cm — release to confirm</span>}
          {tool==='measure' && measureResult && !measureDrag && <span style={{fontSize:9,color:'#22c55e',marginLeft:4}}>✓ {measureResult.dist} cm — double-click canvas to save · drag again for new</span>}
          {tool==='reshape' && <span style={{fontSize:9,color:'#c084fc',marginLeft:4}}>Drag a node to move it · double-click an edge to add a node · select a node for more options →</span>}
        </div>

        {/* Reshape sub-toolbar — only shown while the Reshape tool is active */}
        {tool==='reshape' && (
          <div style={{display:'flex',gap:8,background:'#faf5ff',border:'1px solid #e9d5ff',padding:'6px 10px',borderRadius:8,flexWrap:'wrap',alignItems:'center'}}>
            <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#7c3aed',fontWeight:'bold'}}>
              <input type="checkbox" checked={proportional} onChange={e=>setProportional(e.target.checked)}/> Proportional drag
            </label>
            {proportional && (
              <label style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#7c3aed'}}>
                Falloff
                <input type="number" min={1} max={8} value={propRadius}
                  onChange={e=>setPropRadius(Math.max(1,Math.min(8,+e.target.value||1)))}
                  style={{width:36,padding:'2px 4px',border:'1px solid #d8b4fe',borderRadius:4,fontSize:9}}/>
                nodes
              </label>
            )}
            <div style={{width:1,alignSelf:'stretch',background:'#e9d5ff'}}/>
            <button onClick={toggleNodeSmooth} disabled={reshapeSel==null}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:reshapeSel==null?'not-allowed':'pointer',
                background:reshapeSel==null?'#f3f4f6':'#ede9fe',color:reshapeSel==null?'#9ca3af':'#6d28d9',border:'1px solid #ddd6fe'}}>
              {reshapeSel!=null && pat?.tracedPoints?.[reshapeSel]?.smooth ? '◇ Make Corner' : '◠ Make Smooth'}
            </button>
            <button onClick={deleteNode} disabled={reshapeSel==null}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:reshapeSel==null?'not-allowed':'pointer',
                background:reshapeSel==null?'#f3f4f6':'#fee2e2',color:reshapeSel==null?'#9ca3af':'#dc2626',border:'1px solid #fecaca'}}>
              ✕ Delete Node
            </button>
            <div style={{width:1,alignSelf:'stretch',background:'#e9d5ff'}}/>
            <button onClick={saveAsStandard} disabled={savingStandard}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'#7c3aed',color:'white',border:'none'}}>
              {savingStandard ? '⟳ Saving…' : '💾 Save as Standard'}
            </button>
            <button onClick={resetToGenerated}
              style={{padding:'3px 9px',fontSize:9,fontWeight:'bold',borderRadius:4,cursor:'pointer',background:'rgba(100,116,139,0.1)',color:'#64748b',border:'1px solid #cbd5e1'}}>
              ↺ Reset Shape
            </button>
            {reshapeSel!=null && <span style={{fontSize:9,color:'#7c3aed',marginLeft:'auto'}}>Node {reshapeSel+1} of {pts.length} selected</span>}
          </div>
        )}

        {/* Scale indicator */}
        <div style={{background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:5,padding:'3px 10px',fontSize:9,color:'#166534',display:'flex',gap:12,alignItems:'center'}}>
          <span>📐 <strong>Scale:</strong> {scaleLabel}</span>
          <span>{hasTp ? '✓ Using traced/hand-edited shape' : fromSavedTemplate ? '★ Using saved standard shape' : '⚠ Using generic shape (no tracedPoints)'}</span>
          <span>Pattern: {(W*2).toFixed(1)} × {H.toFixed(1)} cm</span>
        </div>

        {/* SVG canvas */}
        <div style={{flex:1,minHeight:0,border:'1px solid #334155',borderRadius:8,overflow:'hidden',background:'#f8fafc',cursor:tool==='select'?'grab':tool==='notch'?'crosshair':'cell'}}
          onWheel={onWheel}>
          <svg ref={svgRef} width="100%" height="100%"
            viewBox={`0 0 ${VW.toFixed(0)} ${VH.toFixed(0)}`}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
            onClick={handleSvgClick} onDoubleClick={handleDblClick}>

            {/* Grid */}
            {Array.from({length:Math.ceil(VW/SC/10)+1},(_,i)=>(
              <line key={`gx${i}`} x1={txFn(-W+i*10)} y1={0} x2={txFn(-W+i*10)} y2={VH}
                stroke="#e2e8f0" strokeWidth="0.4" strokeDasharray="2,4"/>
            ))}

            {/* Pattern */}
            <PatternSvgInline
              pattern={{ ...pat, anchors: showAnchors ? (buildDisplayPoly(pat).anchors || []) : [] }}
              showSA={showSA}
              measurePts={{ liveLine: liveMeasure, confirmed: measureResult }}
              measureResult={null}
              SCALE={SC} OX={OX} OY={OY} />

            {/* Snap preview dot on edge */}
            {snapLine && (
              <g>
                <circle cx={snapLine.snapX} cy={snapLine.snapY} r={Math.max(3,SC*0.5)}
                  fill="none" stroke="#f59e0b" strokeWidth={Math.max(1,SC*0.18)} strokeDasharray={`${SC*0.6},${SC*0.4}`} opacity="0.9"/>
                <circle cx={snapLine.snapX} cy={snapLine.snapY} r={Math.max(1.5,SC*0.18)} fill="#f59e0b"/>
                <line x1={snapLine.snapX-SC*0.7} y1={snapLine.snapY} x2={snapLine.snapX+SC*0.7} y2={snapLine.snapY} stroke="#f59e0b" strokeWidth={Math.max(0.6,SC*0.12)} opacity="0.7"/>
                <line x1={snapLine.snapX} y1={snapLine.snapY-SC*0.7} x2={snapLine.snapX} y2={snapLine.snapY+SC*0.7} stroke="#f59e0b" strokeWidth={Math.max(0.6,SC*0.12)} opacity="0.7"/>
              </g>
            )}

            {/* Reshape tool: draggable outline nodes + bezier handles */}
            {tool==='reshape' && pts.map((_, i) => {
              const [nx, ny] = nodeScreenPos(i);
              const meta = ptsMeta?.[i];
              const isSel = reshapeSel === i;
              const r = Math.max(3.5, Math.min(6, HIT_R * 0.6));
              return (
                <g key={`node${i}`}>
                  {/* Handle stems + squares for a smooth node */}
                  {meta?.smooth && ['hOut','hIn'].map(side => {
                    const hp = handleScreenPos(i, side);
                    if (!hp) return null;
                    return (
                      <g key={side}>
                        <line x1={nx} y1={ny} x2={hp[0]} y2={hp[1]} stroke="#a855f7" strokeWidth="1" strokeDasharray="2,2" opacity="0.7"/>
                        <rect x={hp[0]-3.5} y={hp[1]-3.5} width="7" height="7" fill="white" stroke="#a855f7" strokeWidth="1.3"
                          style={{cursor:'pointer'}}/>
                      </g>
                    );
                  })}
                  <circle cx={nx} cy={ny} r={r}
                    fill={isSel ? '#a855f7' : (meta?.smooth ? '#c4b5fd' : 'white')}
                    stroke="#7c3aed" strokeWidth={isSel ? 2 : 1.3}
                    style={{cursor:'move'}}/>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Calculated measurements panel */}
        {calcResults && (
          <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:'7px 12px',fontSize:10,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontWeight:'bold',color:'#92400e'}}>📐 Calculated:</span>
            {Object.entries(calcResults).filter(([k])=>/\s/.test(k)).map(([k,v])=>(
              <span key={k} style={{color:'#78350f'}}>
                <strong>{k}</strong>: {v} cm
              </span>
            ))}
            <button onClick={()=>setCalcResults(null)}
              style={{marginLeft:'auto',padding:'2px 8px',background:'#fef3c7',border:'none',borderRadius:3,cursor:'pointer',fontSize:9}}>✕</button>
          </div>
        )}

        {/* Measurement save bar */}
        {measureResult && (
          <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:6,padding:'6px 10px',fontSize:11,display:'flex',gap:10,alignItems:'center'}}>
            📏 <strong>{measureResult.dist} cm</strong>
            <button onClick={addMeasurementAnnotation}
              style={{padding:'3px 10px',background:'#3b82f6',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:10}}>
              Save annotation
            </button>
            <button onClick={()=>setMeasureResult(null)}
              style={{padding:'3px 8px',background:'#f1f5f9',border:'none',borderRadius:4,cursor:'pointer',fontSize:10}}>Discard</button>
          </div>
        )}
      </div>

      {/* Right: properties */}
      <div style={{width:210,flexShrink:0,overflowY:'auto',display:'flex',flexDirection:'column',gap:8}}>

        {/* Seam Allowances */}
        <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#64748b',textTransform:'uppercase',marginBottom:6}}>Seam Allow. (cm)</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
            {['top','right','bottom','left'].map(side=>(
              <div key={side}>
                <label style={{fontSize:9,color:'#94a3b8',textTransform:'capitalize',display:'block'}}>{side}</label>
                <input type="number" step="0.1" min="0" max="5"
                  value={pat.seamAllowances?.[side]??1}
                  onChange={e=>updateSA(side,e.target.value)}
                  style={{width:'100%',padding:'3px 4px',border:'1px solid #ddd',borderRadius:3,fontSize:11,boxSizing:'border-box'}}/>
              </div>
            ))}
          </div>
        </div>

        {/* Notches */}
        <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#64748b',textTransform:'uppercase',marginBottom:5}}>Notches ({(pat.notches||[]).length})</div>
          {(pat.notches||[]).length===0
            ? <div style={{fontSize:9,color:'#94a3b8'}}>Use Notch tool — snaps to edge.</div>
            : <>
              <button onClick={()=>{pushHistory();setPatterns(prev=>prev.map((p,ii)=>ii===selIdx?{...p,notches:[]}:p));}}
                style={{width:'100%',padding:'3px',background:'#fef2f2',border:'1px solid #fecaca',borderRadius:3,cursor:'pointer',fontSize:9,color:'#dc2626',marginBottom:4}}>
                ✕ Clear All {(pat.notches||[]).length} Notches
              </button>
              {(pat.notches||[]).map((n,i)=>(
              <div key={n.id||i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:3,fontSize:9}}>
                <span style={{color:'#475569'}}>{n.type} edge{n.edgeIdx} t={n.t?.toFixed(2)}</span>
                <button onClick={()=>setPatterns(prev=>prev.map((p,ii)=>
                  ii===selIdx?{...p,notches:(p.notches||[]).filter(nn=>nn.id!==n.id)}:p
                ))} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',fontSize:9}}>✕</button>
              </div>
            ))
            }</>
          }
        </div>

        {/* Measurements */}
        <div style={{background:'white',border:'1px solid #e2e8f0',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#64748b',textTransform:'uppercase',marginBottom:5}}>Measurements ({(pat.measurementPoints||[]).length})</div>
          {(pat.measurementPoints||[]).map(mp=>(
            <div key={mp.id} style={{fontSize:9,color:'#475569',marginBottom:3,display:'flex',justifyContent:'space-between'}}>
              <span>{mp.label}: {mp.dist?.toFixed(1)} cm</span>
              <button onClick={()=>setPatterns(prev=>prev.map((p,i)=>
                i===selIdx?{...p,measurementPoints:(p.measurementPoints||[]).filter(m=>m.id!==mp.id)}:p
              ))} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',fontSize:8}}>✕</button>
            </div>
          ))}
        </div>

        {/* ── SPEC BUILDER ─────────────────────────────────────────────── */}
        <div style={{background:'#faf5ff',border:'2px solid #7c3aed',borderRadius:8,padding:10}}>
          <div style={{fontSize:10,fontWeight:'bold',color:'#7c3aed',marginBottom:4,display:'flex',justifyContent:'space-between'}}>
            📐 SPEC BUILDER
            <span style={{fontSize:8,color:'#94a3b8',fontWeight:'normal'}}>edits update shape live</span>
          </div>
          <div style={{fontSize:8,color:'#6d28d9',background:'#ede9fe',borderRadius:4,padding:'3px 6px',marginBottom:6}}>
            {hasTp ? 'Traced/hand-edited shape active — specs used for grading only' : fromSavedTemplate ? 'Using a saved standard shape — specs used for grading only' : 'No trace — specs drive the displayed shape'}
          </div>
          <div style={{maxHeight:180,overflowY:'auto',marginBottom:6}}>
            {Object.entries(pat.measurements||{}).length === 0 && (
              <div style={{fontSize:9,color:'#94a3b8',textAlign:'center',padding:'8px 0'}}>
                No spec values yet. Add below.
              </div>
            )}
            {Object.entries(pat.measurements||{}).map(([k,v])=>(
              <div key={k} style={{display:'flex',gap:4,alignItems:'center',marginBottom:4}}>
                <span style={{fontSize:9,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'#4c1d95',fontWeight:'bold'}}>{k}</span>
                <input type="number" step="0.5" value={v}
                  onChange={e=>updateSpec(k,e.target.value)}
                  style={{width:52,padding:'3px 4px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:10,fontWeight:'bold',textAlign:'right'}}/>
                <span style={{fontSize:8,color:'#7c3aed'}}>cm</span>
                <button onClick={()=>setPatterns(prev=>prev.map((p,i)=>{
                  if(i!==selIdx) return p;
                  const m={...p.measurements}; delete m[k];
                  return {...p,measurements:m,updatedOn:new Date().toISOString()};
                }))} style={{background:'none',border:'none',cursor:'pointer',color:'#dc2626',fontSize:9,lineHeight:1}}>✕</button>
              </div>
            ))}
          </div>
          {/* Add new spec field */}
          <div style={{borderTop:'1px solid #ddd6fe',paddingTop:6}}>
            <div style={{fontSize:8,color:'#6d28d9',marginBottom:3}}>Add measurement:</div>
            <div style={{display:'flex',gap:3}}>
              <input placeholder="e.g. Chest Width" value={editSpecKey} onChange={e=>setEditSpecKey(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addSpec()}
                style={{flex:1,padding:'4px 5px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:9}}/>
              <input type="number" placeholder="cm" value={editSpecVal} onChange={e=>setEditSpecVal(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&addSpec()}
                style={{width:46,padding:'4px 4px',border:'1px solid #c4b5fd',borderRadius:3,fontSize:9}}/>
              <button onClick={addSpec}
                style={{padding:'4px 8px',background:'#7c3aed',color:'white',border:'none',borderRadius:4,cursor:'pointer',fontSize:10,fontWeight:'bold'}}>+</button>
            </div>
          </div>
          {/* Shape key mapping hint — shows template-specific key names */}
          {(() => {
            const keyHints = {
              body_front:   'halfChest · bodyLength · armholeDepth · shoulder',
              body_back:    'halfChest · bodyLength · armholeDepth · shoulder',
              sleeve:       'width · sleeveLength · cuffLength',
              trouser_front:'halfWaist · halfHip · inseam · frontRise',
              trouser_back: 'halfWaist · halfHip · inseam · backRise',
              collar:       'width · collarHeight',
              collar_stand: 'width · collarStandHeight',
              collar_polo:  'width · collarHeight',
              cuff:         'cuffLength · cuffHeight',
              yoke:         'yokeWidth · yokeHeight',
            };
            const tplNow = detectTemplate(pat);
            const hint   = keyHints[tplNow] || 'width · height';
            return (
              <div style={{marginTop:6,fontSize:8,borderRadius:4,padding:'5px 7px',lineHeight:1.8,
                background: hasTp ? '#f0fdf4' : '#ede9fe',
                color: hasTp ? '#166534' : '#6d28d9'}}>
                {hasTp
                  ? <>✓ <strong>Traced shape active</strong> — specs used for grading only, not display.</>
                  : <>
                      <strong>Detected piece:</strong> <span style={{fontFamily:'monospace',color:'#4c1d95'}}>{tplNow}</span><br/>
                      <strong>Keys that drive this shape:</strong><br/>
                      <span style={{fontFamily:'monospace',color:'#4c1d95',fontSize:9}}>{hint}</span><br/>
                      <span style={{color:'#7c3aed'}}>Rename your spec fields to match these keys → shape updates live.</span>
                    </>
                }
              </div>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

// ============================================================================
//  STEP 6 — GRADING VIEW
//  Import grading rules from customer spec data. Apply to create size patterns.
//  Display as nested layers (one on top of another, different strokes per size).
// ============================================================================
const GradingView = ({ patterns, specData, gradingRules, setGradingRules }) => {
  const [selPatIdx, setSelPatIdx] = useState(0);
  const [activeSizes, setActiveSizes] = useState(['S', 'M', 'L', 'XL']);
  const [showAllSizes, setShowAllSizes] = useState(true);
  const [editSize, setEditSize] = useState('M');

  const sizeColors = {
    XS: '#a78bfa', S: '#60a5fa', M: '#34d399', L: '#fbbf24', XL: '#f87171', XXL: '#e879f9', XXXL: '#94a3b8',
  };

  const pat = patterns[selPatIdx];
  if (!pat) return <div style={{ padding: 16, color: '#94a3b8' }}>No patterns loaded.</div>;

  const patRules = gradingRules[pat.id] || {};

  const updateRule = (size, key, val) => {
    setGradingRules(prev => ({
      ...prev,
      [pat.id]: {
        ...prev[pat.id],
        [size]: {
          ...(prev[pat.id]?.[size] || {}),
          [key]: parseFloat(val) || 0,
        }
      }
    }));
  };

  // Auto-derive grading rules from spec sheet size data
  const autoGrade = () => {
    const sizes = specData?.sizes || {};
    const base = specData?.base || {};
    if (Object.keys(sizes).length === 0) {
      alert('No size-wise data in spec sheet. Enter manually or import a spec sheet with size columns.');
      return;
    }
    const newRules = {};
    Object.entries(sizes).forEach(([sz, sizeM]) => {
      newRules[sz] = {};
      Object.keys(pat.measurements || {}).forEach(mk => {
        const specKey = Object.keys(sizeM).find(sk => normKey(sk) === normKey(mk) || normKey(sk).includes(normKey(mk)));
        if (specKey && sizeM[specKey] && base[specKey]) {
          newRules[sz][mk] = +(sizeM[specKey] - base[specKey]).toFixed(2);
        }
      });
    });
    setGradingRules(prev => ({ ...prev, [pat.id]: newRules }));
  };

  const measurementKeys = Object.keys(pat.measurements || {});

  return (
    <div style={{ padding: 16 }}>
      {/* ── STANDARD GRADING RULES LOADER ────────────────────────────── */}
      {(() => {
        const [stdProduct, setStdProduct] = React.useState('');
        const [stdLoaded, setStdLoaded]   = React.useState('');
        const loadStandard = () => {
          if (!stdProduct) return;
          const table = getGradingTable(stdProduct);
          const newRules = {};
          // Apply to current pattern
          patterns.forEach(pat => {
            newRules[pat.id] = {};
            GARMENT_SIZES.forEach(sz => {
              newRules[pat.id][sz] = {};
              Object.entries(table).forEach(([mk, szDeltas]) => {
                if (szDeltas[sz] !== undefined && szDeltas[sz] !== 0) {
                  newRules[pat.id][sz][mk] = szDeltas[sz];
                }
              });
            });
          });
          setGradingRules(prev => ({ ...prev, ...newRules }));
          setStdLoaded(stdProduct);
        };
        const loadPieces = () => {
          if (!stdProduct) return;
          const newPieces = buildStandardPatterns(stdProduct, 'M');
          if (window.confirm(`This will REPLACE all ${newPieces.length} loaded patterns with standard ${GARMENT_STANDARDS[stdProduct]?.label} pieces. Continue?`)) {
            setPatterns(newPieces);
            setStdLoaded(stdProduct);
          }
        };
        return (
          <div style={{ background:'#0f172a', border:'1px solid #334155', borderRadius:8, padding:12, marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:'bold', color:'#38bdf8', marginBottom:8 }}>
              📋 Standard Grading Rules — Load ISO / Industry Standard
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <select value={stdProduct} onChange={e=>setStdProduct(e.target.value)}
                style={{ padding:'6px 10px', background:'#1e293b', color:'white', border:'1px solid #334155', borderRadius:6, fontSize:11 }}>
                <option value="">── Select Product ──</option>
                {Object.entries(GARMENT_STANDARDS).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={loadStandard} disabled={!stdProduct}
                style={{ padding:'6px 14px', background: stdProduct?'#2563eb':'#334155', color:'white', border:'none', borderRadius:6, cursor:stdProduct?'pointer':'not-allowed', fontSize:11, fontWeight:'bold' }}>
                ⚡ Load Grade Rules
              </button>
              <button onClick={loadPieces} disabled={!stdProduct}
                style={{ padding:'6px 14px', background: stdProduct?'#059669':'#334155', color:'white', border:'none', borderRadius:6, cursor:stdProduct?'pointer':'not-allowed', fontSize:11 }}>
                📐 Load Standard Pieces
              </button>
              {stdLoaded && (
                <span style={{ fontSize:10, color:'#34d399', background:'rgba(52,211,153,0.1)', padding:'3px 8px', borderRadius:4 }}>
                  ✓ {GARMENT_STANDARDS[stdLoaded]?.label} rules loaded
                </span>
              )}
            </div>
            <div style={{ fontSize:9, color:'#475569', marginTop:6 }}>
              <strong style={{color:'#64748b'}}>⚡ Load Grade Rules</strong> — fills the delta table below with ISO-standard increments for all sizes.<br/>
              <strong style={{color:'#64748b'}}>📐 Load Standard Pieces</strong> — replaces loaded patterns with the full set of standard cut parts for this garment.
            </div>
          </div>
        );
      })()}

      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4 }}>
        📏 Grading View — Size Nesting
      </div>
      <div style={{ display: 'flex', gap: 16 }}>
        {/* Part selector */}
        <div style={{ width: 140, flexShrink: 0 }}>
          {patterns.map((p, i) => (
            <button key={p.id} onClick={() => setSelPatIdx(i)}
              style={{ width: '100%', textAlign: 'left', padding: '5px 8px', marginBottom: 3, borderRadius: 5,
                border: i === selPatIdx ? '1px solid #7c3aed' : '1px solid #e2e8f0',
                background: i === selPatIdx ? '#f5f3ff' : 'white', cursor: 'pointer', fontSize: 11 }}>
              {p.partName}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }}>
          {/* Size toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 10, color: '#64748b', marginRight: 4 }}>Show sizes:</span>
            {GARMENT_SIZES.map(sz => (
              <label key={sz} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={activeSizes.includes(sz)}
                  onChange={e => setActiveSizes(p => e.target.checked ? [...p, sz] : p.filter(x => x !== sz))} />
                <span style={{ color: sizeColors[sz], fontWeight: 'bold' }}>{sz}</span>
              </label>
            ))}
            <button onClick={autoGrade}
              style={{ marginLeft: 'auto', padding: '4px 10px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
              ⚡ Auto-grade from spec
            </button>
          </div>

          {/* Nested SVG layers — real polygon, proportionally scaled per graded size */}
          <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 8, marginBottom: 12 }}>
            <svg width="100%" height={300} style={{ overflow: 'visible' }}>
              {(() => {
                // Base polygon from actual pattern (tracedPoints or measurement-based)
                const { pts: basePts, W: baseW, H: baseH } = buildDisplayPoly(pat);
                const maxH = baseH * 1.25; // canvas height in cm
                const SC   = Math.min(240 / (baseW * 2 + 8), 200 / maxH, 4);
                const OX   = baseW * SC + 20, OY = 10;
                const tx = x => OX + x * SC;
                const ty = y => OY + maxH * SC - y * SC;
                const baseKeys = Object.keys(pat.measurements || {});

                return activeSizes.map((sz) => {
                  const rules   = patRules[sz] || {};
                  const gradedM = { ...pat.measurements };
                  Object.entries(rules).forEach(([k, d]) => {
                    if (gradedM[k] !== undefined) gradedM[k] = +(gradedM[k] + d).toFixed(2);
                  });

                  // Compute scale from measurement ratios:
                  // Width-type keys scale x, height-type keys scale y.
                  // Fallback: uniform average scale.
                  const WK = /width|chest|hip|waist|bust|circumference|neck|shoulder|cuff/i;
                  const HK = /height|body|arm|sleeve|inseam|rise|back|front|depth/i;
                  let wBase=0,wGrd=0,wN=0, hBase=0,hGrd=0,hN=0, aBase=0,aGrd=0,aN=0;
                  baseKeys.forEach(k => {
                    const bv = pat.measurements[k]||0;
                    const gv = gradedM[k]||0;
                    if (WK.test(k))      { wBase+=bv; wGrd+=gv; wN++; }
                    else if (HK.test(k)) { hBase+=bv; hGrd+=gv; hN++; }
                    else                 { aBase+=bv; aGrd+=gv; aN++; }
                  });
                  // "length" is ambiguous — check if it appeared in any rule delta
                  // and distribute evenly to both axes if no explicit width/height match
                  let scaleX = wN>0&&wBase>0 ? wGrd/wBase : 1;
                  let scaleY = hN>0&&hBase>0 ? hGrd/hBase : 1;
                  if (scaleX===1 && scaleY===1 && aN>0 && aBase>0) {
                    // All keys are ambiguous — apply uniform scale
                    const u = aGrd/aBase;
                    scaleX = u; scaleY = u;
                  }
                  // If we STILL have no scale (no measurements defined), check
                  // if any rule exists and use a tiny default increment
                  if (scaleX===1 && scaleY===1 && Object.keys(rules).length>0) {
                    const totalBase = baseKeys.reduce((s,k)=>s+(pat.measurements[k]||0),0)||1;
                    const totalGrd  = baseKeys.reduce((s,k)=>s+(gradedM[k]||0),0)||1;
                    scaleX = scaleY = totalGrd/totalBase;
                  }

                  const gradedPts = basePts.map(([x,y]) => [x*scaleX, y*scaleY]);
                  const polyStr   = gradedPts.map(([x,y]) => `${tx(x).toFixed(1)},${ty(y).toFixed(1)}`).join(' ');
                  const isBase    = sz === 'M';
                  const labelX    = tx(baseW * scaleX + 3);
                  const labelY    = ty(baseH * scaleY * 0.5);

                  return (
                    <g key={sz}>
                      <polygon points={polyStr} fill={isBase ? `${sizeColors[sz]}22` : 'none'}
                        stroke={sizeColors[sz]}
                        strokeWidth={isBase ? 2 : 1}
                        strokeDasharray={isBase ? 'none' : '5,2'}
                        opacity={isBase ? 1 : 0.85} />
                      <text x={labelX} y={labelY} fontSize="9" fill={sizeColors[sz]} fontWeight={isBase?'bold':'normal'}>
                        {sz} {scaleX!==1||scaleY!==1 ? `×${((scaleX+scaleY)/2).toFixed(3)}` : '(base)'}
                      </text>
                    </g>
                  );
                });
              })()}
            </svg>
            <div style={{fontSize:9,color:'#64748b',padding:'4px 6px',textAlign:'center'}}>
              M (filled) = base size · other sizes shown as outlines · right-click on table to add delta
            </div>
          </div>

          {/* Grading rules table */}
          <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 'bold', color: '#475569', marginBottom: 8 }}>
              Grading Deltas (cm) — deviations from base size:
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', fontSize: 10, width: '100%' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left', borderBottom: '1px solid #e2e8f0', minWidth: 100 }}>Measurement</th>
                    {activeSizes.map(sz => (
                      <th key={sz} style={{ padding: '5px 8px', borderBottom: '1px solid #e2e8f0', color: sizeColors[sz], minWidth: 50 }}>{sz}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {measurementKeys.map(mk => (
                    <tr key={mk} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '4px 8px', color: '#475569' }}>{mk}</td>
                      {activeSizes.map(sz => (
                        <td key={sz} style={{ padding: '3px 6px' }}>
                          <input
                            type="number" step="0.5"
                            value={patRules[sz]?.[mk] ?? ''}
                            placeholder="0"
                            onChange={e => updateRule(sz, mk, e.target.value)}
                            style={{ width: 46, padding: '2px 3px', border: '1px solid #ddd', borderRadius: 3, fontSize: 10, textAlign: 'center' }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
//  STEP 7 — SEAM COMPATIBILITY CHECK
//  Runs the matching algorithm: neck vs collar, armhole vs sleeve, etc.
// ============================================================================
const SeamChecker = ({ patterns, gradingRules, activeSizes = ['M'] }) => {
  const [results, setResults] = useState([]);
  const [customRules, setCustomRules] = useState(DEFAULT_SEAM_RULES);
  const [ran, setRan] = useState(false);

  // Extract measurement value for a part (by template or name)
  const getMeasurement = useCallback((patterns, partKeyword, specKey, size) => {
    const pat = patterns.find(p =>
      p.partName?.toLowerCase().includes(partKeyword.toLowerCase()) ||
      p.template?.toLowerCase().includes(partKeyword.toLowerCase())
    );
    if (!pat) return null;
    const rules = gradingRules?.[pat.id]?.[size] || {};
    const base = pat.measurements?.[specKey] ?? null;
    if (base === null) return null;
    return +(base + (rules[specKey] || 0)).toFixed(2);
  }, [gradingRules]);

  const runCheck = () => {
    const checkResults = customRules.map(rule => {
      const sizeResults = activeSizes.map(sz => {
        const valA = getMeasurement(patterns, rule.partA, rule.specA, sz);
        const valB = getMeasurement(patterns, rule.partB, rule.specB, sz);
        if (valA === null || valB === null) {
          return { size: sz, valA, valB, delta: null, status: 'missing' };
        }
        const delta = Math.abs(valA - valB);
        return { size: sz, valA, valB, delta: +delta.toFixed(2), status: delta <= rule.tolerance ? 'ok' : 'fail' };
      });
      return { rule, sizeResults };
    });
    setResults(checkResults);
    setRan(true);
  };

  const statusIcon = (s) => s === 'ok' ? '✓' : s === 'missing' ? '—' : '✗';
  const statusColor = (s) => s === 'ok' ? '#16a34a' : s === 'missing' ? '#94a3b8' : '#dc2626';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 4 }}>
        🔗 Seam Compatibility Check
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
        Verifies that joined seam lengths are compatible (e.g. collar length ≈ neckline measurement).
      </div>

      <button onClick={runCheck}
        style={{ marginBottom: 14, padding: '8px 18px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}>
        ▶ Run Compatibility Check
      </button>

      {ran && (
        <div>
          {results.map((r, i) => {
            const hasFail = r.sizeResults.some(s => s.status === 'fail');
            const hasMissing = r.sizeResults.some(s => s.status === 'missing');
            return (
              <div key={r.rule.id} style={{
                background: hasFail ? '#fef2f2' : hasMissing ? '#fffbeb' : '#f0fdf4',
                border: `1px solid ${hasFail ? '#fecaca' : hasMissing ? '#fde68a' : '#bbf7d0'}`,
                borderRadius: 8, padding: 12, marginBottom: 8,
              }}>
                <div style={{ fontWeight: 'bold', fontSize: 12, color: '#1e293b', marginBottom: 6 }}>
                  {hasFail ? '⚠️' : hasMissing ? '❓' : '✅'} {r.rule.label}
                  <span style={{ fontSize: 9, fontWeight: 'normal', color: '#64748b', marginLeft: 8 }}>
                    tolerance ±{r.rule.tolerance} cm
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {r.sizeResults.map(sr => (
                    <div key={sr.size} style={{ background: 'white', border: `1px solid ${statusColor(sr.status)}`, borderRadius: 6, padding: '5px 10px', minWidth: 80 }}>
                      <div style={{ fontSize: 9, color: '#94a3b8', textAlign: 'center' }}>Size {sr.size}</div>
                      <div style={{ fontSize: 11, textAlign: 'center', fontWeight: 'bold', color: statusColor(sr.status) }}>
                        {statusIcon(sr.status)}
                      </div>
                      {sr.valA !== null && <div style={{ fontSize: 9, color: '#475569', textAlign: 'center' }}>
                        {sr.valA} vs {sr.valB}
                      </div>}
                      {sr.delta !== null && sr.status !== 'ok' && (
                        <div style={{ fontSize: 9, color: '#dc2626', textAlign: 'center' }}>Δ {sr.delta}</div>
                      )}
                    </div>
                  ))}
                </div>
                {hasFail && (
                  <div style={{ marginTop: 8, fontSize: 10, color: '#dc2626', background: '#fff5f5', padding: '6px 8px', borderRadius: 4 }}>
                    ⚠ Fix: Adjust {r.rule.partA} {r.rule.specA} or {r.rule.partB} {r.rule.specB} so they match within ±{r.rule.tolerance} cm.
                    Return to Step 4 (Pattern Editor) to adjust measurements.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  STEP 8 — MARKER PLANNER
//  Three modes: (1) PO color-wise qty ratio, (2) Manual size ratio, (3) Auto
//  Warp-aligned placement, nested fills, yield maximisation.
// ============================================================================

// Polygon area via shoelace formula (cm²)
// Handles both {x,y} objects and [x,y] arrays
const polyArea = (pts) => {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const j  = (i + 1) % pts.length;
    const xi = Array.isArray(pts[i]) ? pts[i][0] : pts[i].x;
    const yi = Array.isArray(pts[i]) ? pts[i][1] : pts[i].y;
    const xj = Array.isArray(pts[j]) ? pts[j][0] : pts[j].x;
    const yj = Array.isArray(pts[j]) ? pts[j][1] : pts[j].y;
    a += xi * yj - xj * yi;
  }
  return Math.abs(a) / 2;
};

// Compute bounding box AND actual polygon for every pattern.
// Uses buildDisplayPoly which handles tracedPoints (real shape) OR
// measurement-based generic shape — so every pattern always gets a polygon.
const getPatternBbox = (pat, scaleFactor = 1) => {
  const { pts: rawPts, W, H } = buildDisplayPoly(pat);
  // rawPts are [[x,y]] centred at x=0; shift x by +W to make 0-based (top-left origin)
  const pts = rawPts.map(([x, y]) => ({
    x: +((x + W) * scaleFactor).toFixed(3),
    y: +(y       * scaleFactor).toFixed(3),
  }));
  const w = +(W * 2 * scaleFactor).toFixed(2);
  const h = +(H       * scaleFactor).toFixed(2);
  return { w: Math.max(w, 5), h: Math.max(h, 5), pts };
};

// ── SKYLINE BIN PACKING ─────────────────────────────────────────────────
// Each piece is placed at the lowest available y-position across all
// x-positions — exactly like real CAM marker software.
// The "skyline" tracks the top edge of placed pieces for every cm of width.
// Tall narrow pieces fill the space beside short wide pieces automatically.
const SKYLINE_SLOT = 0.5; // cm resolution for the skyline scan

const binPackMarker = (pieces, fabricWidth, fabricMaxLength, gap = 0.5, iterations = 3) => {
  // Run multiple times with different sort orders; keep best yield
  const orders = [
    [...pieces].sort((a, b) => b.h  - a.h  || b.w - a.w),   // tallest first
    [...pieces].sort((a, b) => b.w  - a.w  || b.h - a.h),   // widest first
    [...pieces].sort((a, b) => (b.w * b.h) - (a.w * a.h)),  // largest area first
  ];

  let best = null;

  for (let iter = 0; iter < Math.min(orders.length, iterations); iter++) {
    const sorted = orders[iter];
    const numSlots = Math.ceil(fabricWidth / SKYLINE_SLOT);
    // heights[s] = current top of the skyline at slot s (in cm)
    const heights = new Float32Array(numSlots).fill(0);
    const placed  = [];

    for (const piece of sorted) {
      const pw = piece.w + gap;
      const ph = piece.h + gap;
      const nw = Math.max(1, Math.ceil(pw / SKYLINE_SLOT));

      // Scan every valid x start position and find the one with
      // the minimum max-height under the piece's footprint
      let bestX = 0, bestY = Infinity;
      for (let s = 0; s + nw <= numSlots; s++) {
        let maxH = 0;
        for (let j = s; j < s + nw; j++) {
          if (heights[j] > maxH) maxH = heights[j];
        }
        if (maxH < bestY) { bestY = maxH; bestX = s * SKYLINE_SLOT; }
      }

      if (fabricMaxLength > 0 && bestY + ph > fabricMaxLength) continue;

      placed.push({ ...piece, x: +bestX.toFixed(3), y: +bestY.toFixed(3) });

      // Update skyline under this piece
      const s0 = Math.floor(bestX / SKYLINE_SLOT);
      const s1 = Math.min(numSlots, s0 + nw);
      const newH = bestY + ph;
      for (let j = s0; j < s1; j++) heights[j] = newH;
    }

    // Score this iteration
    const usedLen = placed.length > 0
      ? placed.reduce((m, p) => Math.max(m, p.y + p.h), 0) : 0;
    const totalArea = placed.reduce((s, p) => s + polyArea(p.pts || []), 0);
    const layArea   = fabricWidth * usedLen;
    const yieldPct  = layArea > 0 ? +((totalArea / layArea) * 100).toFixed(2) : 0;

    if (!best || yieldPct > best.yieldPct) {
      const lengthPct = fabricMaxLength > 0
        ? +((usedLen / fabricMaxLength) * 100).toFixed(1) : null;
      best = {
        placed,
        usedLength:  +usedLen.toFixed(2),
        yieldPct,
        lengthPct,
        totalPieces: placed.length,
      };
    }
  }

  return best || { placed: [], usedLength: 0, yieldPct: 0, lengthPct: null, totalPieces: 0 };
};

const MarkerPlanner = ({ patterns, specData, gradingRules }) => {
  const [mode, setMode] = useState('manual');  // po | manual | auto
  const [fabricWidth, setFabricWidth] = useState(150);  // cm
  const [fabricMaxLength, setFabricMaxLength] = useState(0);  // 0 = unlimited
  const [gap, setGap] = useState(0.5);
  const [sizeRatio, setSizeRatio] = useState({ XS: 0, S: 1, M: 2, L: 2, XL: 1, XXL: 0 });
  const [poData, setPoData] = useState('');
  const [layout, setLayout] = useState(null);
  const [running, setRunning] = useState(false);
  const [iterations, setIterations] = useState(3);
  const [placedPieces, setPlacedPieces] = useState([]);
  const [selPiece, setSelPiece]   = useState(null);
  const [rotations, setRotations] = useState({});  // pieceKey → 0|90|180|270
  const svgRef = useRef(null);

  const sizeColors = {
    XS: '#a78bfa', S: '#60a5fa', M: '#34d399', L: '#fbbf24', XL: '#f87171', XXL: '#e879f9', XXXL: '#94a3b8',
  };

  // Parse PO mode (CSV: size,qty,color lines)
  const parsePO = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    const out = {};
    lines.forEach(line => {
      const [sz, qty] = line.split(/[,;|\t]/);
      if (sz && qty) out[sz.trim().toUpperCase()] = parseInt(qty) || 0;
    });
    return out;
  };

  const buildPieces = (ratios) => {
    const pieces = [];
    Object.entries(ratios).forEach(([sz, count]) => {
      if (!count) return;
      patterns.forEach(pat => {
        const rules = gradingRules?.[pat.id]?.[sz] || {};
        const gradedM = { ...pat.measurements };
        Object.entries(rules).forEach(([k, d]) => { if (gradedM[k] !== undefined) gradedM[k] = +(gradedM[k] + d).toFixed(2); });
        // Use the actual traced shape for both bbox and polygon rendering
        const fullPat = { ...pat, measurements: gradedM };
        const bbox = getPatternBbox(fullPat);
        for (let c = 0; c < count; c++) {
          pieces.push({ id: `${pat.id}-${sz}-${c}`, partName: pat.partName, size: sz, ...bbox });
        }
      });
    });
    return pieces;
  };

  const runMarker = async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 50));
    try {
      let ratios = { ...sizeRatio };
      if (mode === 'po') ratios = parsePO(poData);
      else if (mode === 'auto') {
        // Auto: equal distribution if no size data
        const sizes = Object.keys(specData?.sizes || { S: 1, M: 2, L: 2, XL: 1 });
        sizes.forEach(sz => { ratios[sz] = (specData?.sizes?.[sz] ? 2 : 1); });
      }

      const pieces = buildPieces(ratios);
      if (pieces.length === 0) { alert('No pieces to place. Check size ratio / pattern definitions.'); setRunning(false); return; }

      // Skyline packer runs multiple sort-order iterations internally
      const bestResult = binPackMarker(pieces, fabricWidth, fabricMaxLength, gap, Math.max(1, iterations));

      setLayout(bestResult);
      setPlacedPieces(bestResult.placed.map(p => ({ ...p })));
    } finally {
      setRunning(false);
    }
  };

  const exportPdf = () => {
    if (!layout) return;
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const svgStr = new XMLSerializer().serializeToString(svgEl);
    const html = `<!DOCTYPE html><html><head><title>Marker Plan</title>
      <style>@page{size:A3 landscape;margin:8mm}body{margin:0;font-family:monospace}
      h3{margin:0 0 4mm;font-size:13pt} .meta{font-size:9pt;margin-bottom:6mm}
      svg{width:100%;height:auto;border:1px solid #ccc}</style></head><body>
      <h3>Marker Plan — ${new Date().toLocaleString()}</h3>
      <div class="meta">
        Fabric: ${fabricWidth} cm wide · Used length: ${layout.usedLength.toFixed(1)} cm ·
        Pieces: ${layout.totalPieces} · Yield: <strong>${layout.yieldPct.toFixed(2)}%</strong>
      </div>
      ${svgStr}
      <script>window.onload=()=>setTimeout(()=>window.print(),300);</script>
      </body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
  };

  // Drag piece on SVG
  const dragRef = useRef(null);
  const getSvgPt = (e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x, y };
  };

  const onPieceDown = (e, idx) => {
    e.stopPropagation();
    const { x, y } = getSvgPt(e);
    dragRef.current = { idx, ox: x - placedPieces[idx].x, oy: y - placedPieces[idx].y };
    setSelPiece(idx);
  };

  const onSvgMove = (e) => {
    if (!dragRef.current) return;
    const { x, y } = getSvgPt(e);
    const { idx, ox, oy } = dragRef.current;
    const p = placedPieces[idx];
    const nx = Math.max(0, Math.min(fabricWidth - p.w, x - ox));
    const ny = Math.max(0, y - oy);
    setPlacedPieces(prev => prev.map((pp, i) => i === idx ? { ...pp, x: +nx.toFixed(2), y: +ny.toFixed(2) } : pp));
  };

  const onSvgUp = () => { dragRef.current = null; };

  // Visual scale
  const SCALE = Math.min(760 / fabricWidth, 4);
  const RULER_W    = 28;   // left ruler width in SVG px
  const dispWidth  = fabricWidth * SCALE;
  const usedLen    = layout?.usedLength || 200;
  const dispHeight = Math.min(usedLen * SCALE, 600);

  return (
    <div style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: '#1e293b', marginBottom: 8 }}>
        🗺️ Marker Planner
      </div>

      {/* Mode selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[
          { id: 'po',     label: '1 — PO Color-Wise Qty',  desc: 'Ratio from Purchase Order' },
          { id: 'manual', label: '2 — Manual Size Ratio',  desc: 'User-specified counts' },
          { id: 'auto',   label: '3 — Auto Mode',          desc: 'System determines from spec / history' },
        ].map(m => (
          <button key={m.id} onClick={() => setMode(m.id)}
            style={{ flex: 1, padding: '8px 10px', textAlign: 'left', fontSize: 11,
              background: mode === m.id ? '#1e293b' : 'white',
              color: mode === m.id ? 'white' : '#334155',
              border: `1px solid ${mode === m.id ? '#1e293b' : '#e2e8f0'}`, borderRadius: 8, cursor: 'pointer' }}>
            <div style={{ fontWeight: 'bold' }}>{m.label}</div>
            <div style={{ fontSize: 9, opacity: 0.7, marginTop: 2 }}>{m.desc}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16 }}>
        {/* Left: Controls */}
        <div>
          {/* Fabric settings */}
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 'bold', color: '#0c4a6e', marginBottom: 6 }}>FABRIC SETTINGS</div>
            {[
              ['Width (cm)', fabricWidth, v => setFabricWidth(+v)],
              ['Max Length (cm, 0=∞)', fabricMaxLength, v => setFabricMaxLength(+v)],
              ['Gap between parts (cm)', gap, v => setGap(+v)],
              ['Optimise iterations', iterations, v => setIterations(+v)],
            ].map(([label, val, setter]) => (
              <div key={label} style={{ marginBottom: 6 }}>
                <label style={{ fontSize: 9, color: '#0c4a6e' }}>{label}</label>
                <input type="number" min={0} step={0.5} value={val} onChange={e => setter(e.target.value)}
                  style={{ width: '100%', padding: '4px 6px', border: '1px solid #bae6fd', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' }} />
              </div>
            ))}
          </div>

          {/* Mode-specific controls */}
          {mode === 'po' && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 'bold', color: '#9a3412', marginBottom: 6 }}>PO SIZE QTY (size,count per line)</div>
              <textarea value={poData} onChange={e => setPoData(e.target.value)} rows={6}
                placeholder={`S,50\nM,100\nL,80\nXL,40`}
                style={{ width: '100%', fontFamily: 'monospace', fontSize: 10, padding: 5, border: '1px solid #fed7aa', borderRadius: 4, boxSizing: 'border-box', resize: 'vertical' }} />
            </div>
          )}

          {mode === 'manual' && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 'bold', color: '#166534', marginBottom: 6 }}>SIZE RATIO (garments per size)</div>
              {GARMENT_SIZES.map(sz => (
                <div key={sz} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <label style={{ fontSize: 10, color: sizeColors[sz], fontWeight: 'bold', width: 30 }}>{sz}</label>
                  <input type="number" min={0} max={20} step={1}
                    value={sizeRatio[sz] ?? 0}
                    onChange={e => setSizeRatio(p => ({ ...p, [sz]: +e.target.value }))}
                    style={{ width: 60, padding: '3px 5px', border: '1px solid #bbf7d0', borderRadius: 4, fontSize: 11 }} />
                </div>
              ))}
            </div>
          )}

          {mode === 'auto' && (
            <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: '#6d28d9' }}>
                Auto mode uses spec sheet size data or historical average to determine size ratio.
                {!specData?.sizes || Object.keys(specData.sizes).length === 0
                  ? ' No spec size data found — will use equal distribution.'
                  : ` Found ${Object.keys(specData.sizes).length} sizes in spec.`}
              </div>
            </div>
          )}

          <button onClick={runMarker} disabled={running}
            style={{ width: '100%', padding: '10px', background: running ? '#94a3b8' : '#0ea5e9', color: 'white',
              border: 'none', borderRadius: 8, cursor: running ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 'bold' }}>
            {running ? '⟳ Computing…' : '▶ Generate Marker'}
          </button>
        </div>

        {/* Right: Layout canvas */}
        <div>
          {layout && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 20, fontSize: 11, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>📐 <strong>Fabric used:</strong> {layout.usedLength.toFixed(1)} cm</span>
              <span>📦 <strong>Pieces:</strong> {layout.totalPieces}</span>
              {layout.lengthPct !== null && (
                <span style={{ color: '#2563eb', fontWeight: 'bold' }}>
                  📏 Length used: {layout.lengthPct}% of max
                </span>
              )}
              <span style={{ color: '#16a34a', fontWeight: 'bold' }}
                title="Pattern area ÷ total lay area. Higher = less fabric waste between pieces.">
                🎯 Fabric utilisation: {layout.yieldPct.toFixed(1)}%
              </span>
              <span style={{ fontSize:9, color:'#94a3b8' }}>Right-click a piece to rotate 90°</span>
              <button onClick={exportPdf}
                style={{ marginLeft: 'auto', padding: '5px 12px', background: '#1e293b', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
                🖨 Print / Export PDF
              </button>
            </div>
          )}

          {/* SVG lay */}
          <div style={{ background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto' }}>
            <svg
              ref={svgRef}
              width={dispWidth + RULER_W}
              height={layout ? dispHeight + 40 : 220}
              style={{ display: 'block' }}
              onMouseMove={onSvgMove}
              onMouseUp={onSvgUp}
              onMouseLeave={onSvgUp}
            >
              {/* Fabric background */}
              <rect x={RULER_W} y={16} width={dispWidth} height={layout ? dispHeight + 20 : 200}
                fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1" />
              {/* Warp lines (vertical dashes = warp direction) */}
              {Array.from({ length: Math.floor(fabricWidth / 10) }, (_, i) => (
                <line key={i} x1={RULER_W + (i + 1) * 10 * SCALE} y1={16} x2={RULER_W + (i + 1) * 10 * SCALE} y2={dispHeight + 36}
                  stroke="#e2e8f0" strokeWidth="0.5" strokeDasharray="2,4" />
              ))}
              {/* WARP direction label (vertical = length = grain direction) */}
              <text x={12} y={dispHeight/2 + 20} textAnchor="middle" fontSize="9" fill="#475569" fontWeight="bold"
                transform={`rotate(-90 12 ${dispHeight/2})`}>
                WARP / LENGTH (cm)
              </text>
              {/* Width label at top */}
              <text x={RULER_W + dispWidth/2} y={12} textAnchor="middle" fontSize="9" fill="#64748b">
                ← {fabricWidth} cm wide (WEFT) →
              </text>
              {/* Length ruler — tick marks every 10 cm on left */}
              {Array.from({length:Math.ceil((layout?.usedLength||200)/10)+1},(_,i)=>{
                const yPx = 18 + i*10*SCALE;
                return (
                  <g key={`ry${i}`}>
                    <line x1={RULER_W-8} y1={yPx} x2={RULER_W} y2={yPx} stroke="#94a3b8" strokeWidth="0.8"/>
                    {i%5===0 && <text x={RULER_W-10} y={yPx+3} textAnchor="end" fontSize="7" fill="#64748b">{i*10}</text>}
                  </g>
                );
              })}

              {/* Placed pieces — real polygon shape + rotation support */}
              {placedPieces.map((p, i) => {
                const fillCol = sizeColors[p.size] || '#94a3b8';
                const rot     = rotations[`${p.id}-${i}`] || 0;
                const cx  = p.x * SCALE + (p.w * SCALE) / 2;
                const cy  = 14 + p.y * SCALE + (p.h * SCALE) / 2;
                // Build polygon from tracedPoints, applying rotation
                let polyStr = null;
                if (p.pts && p.pts.length >= 3) {
                  const rad = rot * Math.PI / 180;
                  const cos_ = Math.cos(rad), sin_ = Math.sin(rad);
                  // rotate each point around piece centre (in cm)
                  const cxCm = p.x + p.w/2, cyCm = p.y + p.h/2;
                  polyStr = p.pts.map(pt => {
                    const rx = pt.x - p.w/2, ry = pt.y - p.h/2;
                    const rotX = rx*cos_ - ry*sin_ + cxCm;
                    const rotY = rx*sin_ + ry*cos_ + cyCm;
                    return `${RULER_W + rotX * SCALE},${18 + rotY * SCALE}`;
                  }).join(' ');
                }
                return (
                  <g key={`${p.id}-${i}`}
                    onMouseDown={e => onPieceDown(e, i)}
                    onContextMenu={e => {
                      e.preventDefault();
                      setRotations(prev => ({...prev, [`${p.id}-${i}`]: ((prev[`${p.id}-${i}`]||0) + 90) % 360 }));
                    }}
                    style={{ cursor: 'grab' }}>
                    {polyStr ? (
                      <polygon points={polyStr}
                        fill={fillCol} fillOpacity={selPiece===i?0.55:0.3}
                        stroke={selPiece===i?'#1e293b':fillCol} strokeWidth={selPiece===i?2:1}/>
                    ) : (
                      <rect x={RULER_W+p.x*SCALE} y={18+p.y*SCALE} width={p.w*SCALE} height={p.h*SCALE}
                        fill={fillCol} fillOpacity={selPiece===i?0.55:0.3}
                        stroke={selPiece===i?'#1e293b':fillCol} strokeWidth={selPiece===i?2:1}/>
                    )}
                    {/* Grain line — vertical through piece centre */}
                    <line x1={cx} y1={18+p.y*SCALE+4} x2={cx} y2={18+(p.y+p.h)*SCALE-4}
                      stroke="rgba(5,150,105,0.6)" strokeWidth="0.9" strokeDasharray="3,2"/>
                    {p.w*SCALE>18 && p.h*SCALE>14 && (
                      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
                        fontSize={Math.max(6,Math.min(8,p.w*SCALE/6))} fill="#1e293b">
                        {p.partName?.substring(0,8)} {p.size}{rot?` ↺${rot}°`:''}
                      </text>
                    )}
                  </g>
                );
              })}

              {!layout && (
                <text x={dispWidth / 2} y={110} textAnchor="middle" fontSize="11" fill="#94a3b8">
                  Configure settings and click "Generate Marker"
                </text>
              )}
            </svg>
          </div>

          {/* Legend */}
          {layout && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {Object.entries(sizeColors).filter(([sz]) =>
                placedPieces.some(p => p.size === sz)
              ).map(([sz, color]) => (
                <span key={sz} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#475569' }}>
                  <span style={{ width: 12, height: 12, background: color, opacity: 0.5, borderRadius: 2, display: 'inline-block', border: `1px solid ${color}` }} />
                  {sz} ({placedPieces.filter(p => p.size === sz).length} pcs)
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
//  MAIN — MarkerMaker CONTAINER
// ============================================================================

// ============================================================================
//  PATTERN GALLERY
//  Shows ALL stored patterns as cards — review, edit, delete, organise.
//  Accessible from the main MarkerMaker top bar.
// ============================================================================
const PatternGallery = ({ db, onClose, onEdit }) => {
  const [patterns,  setPatterns]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState({ search:'', template:'' });
  const [selIdx,    setSelIdx]    = useState(null);
  const [deleting,  setDeleting]  = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/get_patterns.php`)
      .then(r => r.json())
      .then(d => { if (d.success) setPatterns(d.patterns || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const deletePattern = async (p) => {
    if (!window.confirm(`Delete pattern "${p.partName}"?`)) return;
    setDeleting(p.id);
    try {
      await fetch(`${API_BASE}/save_universal.php`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ category:'pattern', data:{ dbId: p.dbId, _deleted: true } })
      });
      setPatterns(prev => prev.filter(pp => pp.id !== p.id));
    } finally { setDeleting(null); }
  };

  // ── Mini SVG thumbnail for each card ─────────────────────────────────────
  const Thumb = ({ pattern }) => {
    const { pts, W, H } = buildDisplayPoly(pattern);
    const PAD = 6, TW = 120, TH = 90;
    const sc = Math.min((TW - PAD*2) / (W*2), (TH - PAD*2) / H, 4);
    const tx = x => TW/2 + x * sc;
    const ty = y => TH - PAD - y * sc;
    return (
      <svg width={TW} height={TH} style={{ display:'block', background:'#f8fafc', borderRadius:4 }}>
        {/* Mini grid */}
        {Array.from({length: Math.ceil(W*2/5)+1}, (_,i) => (
          <line key={i} x1={tx(-W + i*5)} y1={PAD} x2={tx(-W + i*5)} y2={TH-PAD}
            stroke="#e2e8f0" strokeWidth="0.4"/>
        ))}
        <polygon points={pts.map(p=>`${tx(p[0]).toFixed(1)},${ty(p[1]).toFixed(1)}`).join(' ')}
          fill="rgba(37,99,235,0.07)" stroke="#1e3a8a" strokeWidth="1" strokeLinejoin="round"/>
        {/* Grain line */}
        <line x1={tx(0)} y1={ty(H*0.15)} x2={tx(0)} y2={ty(H*0.85)}
          stroke="#059669" strokeWidth="0.8" strokeDasharray="3,2"/>
        <text x={TW/2} y={TH-1} textAnchor="middle" fontSize="7" fill="#64748b">{pattern.partName}</text>
      </svg>
    );
  };

  const templates = [...new Set(patterns.map(p => detectTemplate(p)).filter(Boolean))];
  const filtered = patterns.filter(p => {
    const s = `${p.partName} ${p.template} ${p.sourceMethod||''}`.toLowerCase();
    return (!filter.search || s.includes(filter.search.toLowerCase()))
      && (!filter.template || detectTemplate(p) === filter.template);
  });

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10000, background:'#0f172a', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ background:'#1e293b', padding:'10px 20px', display:'flex', alignItems:'center', gap:12, borderBottom:'1px solid #334155', flexShrink:0 }}>
        <span style={{ fontSize:16, fontWeight:'bold', color:'white' }}>📐 Pattern Library</span>
        <span style={{ fontSize:11, color:'#64748b' }}>{patterns.length} patterns stored across all parts</span>
        {/* Search */}
        <input value={filter.search} onChange={e=>setFilter(f=>({...f,search:e.target.value}))}
          placeholder="Search by name…"
          style={{ marginLeft:'auto', padding:'5px 10px', background:'#334155', color:'white', border:'1px solid #475569', borderRadius:6, fontSize:11, width:180 }}/>
        <select value={filter.template} onChange={e=>setFilter(f=>({...f,template:e.target.value}))}
          style={{ padding:'5px 8px', background:'#334155', color:'white', border:'1px solid #475569', borderRadius:6, fontSize:11 }}>
          <option value="">All types</option>
          {templates.map(t=><option key={t} value={t}>{t.replace(/_/g,' ').toUpperCase()}</option>)}
        </select>
        <button onClick={onClose}
          style={{ padding:'6px 14px', background:'#ef4444', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:'bold' }}>
          ✕ Close
        </button>
      </div>

      {/* Gallery grid */}
      <div style={{ flex:1, overflowY:'auto', padding:20 }}>
        {loading && <div style={{ color:'#64748b', textAlign:'center', padding:40 }}>Loading patterns…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ color:'#475569', textAlign:'center', padding:60, fontSize:13 }}>
            No patterns found.{filter.search || filter.template ? ' Try clearing filters.' : ' Add patterns via PartDNA → Patterns & Specs tab.'}
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:12 }}>
          {filtered.map((p, i) => {
            const tpl = detectTemplate(p);
            const { W, H } = buildDisplayPoly(p);
            return (
              <div key={p.id || i}
                style={{ background:'#1e293b', border:`1px solid ${selIdx===i?'#3b82f6':'#334155'}`, borderRadius:10, overflow:'hidden', cursor:'pointer', transition:'border 0.15s' }}
                onClick={() => setSelIdx(selIdx===i ? null : i)}>
                {/* Thumbnail */}
                <div style={{ padding:8, background:'#0f172a', display:'flex', justifyContent:'center' }}>
                  <Thumb pattern={p} />
                </div>
                {/* Info */}
                <div style={{ padding:'8px 10px' }}>
                  <div style={{ fontWeight:'bold', fontSize:12, color:'white', marginBottom:2 }}>{p.partName}</div>
                  <div style={{ fontSize:9, color:'#64748b', marginBottom:4 }}>
                    <span style={{ background:'#334155', padding:'1px 5px', borderRadius:3, marginRight:4 }}>{tpl.replace(/_/g,' ')}</span>
                    {p.hasTp !== false && <span style={{ color:'#34d399' }}>● traced</span>}
                  </div>
                  <div style={{ fontSize:9, color:'#94a3b8' }}>
                    {(W*2).toFixed(1)} × {H.toFixed(1)} cm · SA: {Object.values(p.seamAllowances||{}).join('/')} · Notches: {(p.notches||[]).length}
                  </div>
                  <div style={{ fontSize:9, color:'#64748b', marginTop:2 }}>
                    {Object.keys(p.measurements||{}).length} specs · Updated: {p.updatedOn ? new Date(p.updatedOn).toLocaleDateString() : '—'}
                  </div>
                  {/* Actions */}
                  <div style={{ display:'flex', gap:4, marginTop:8 }}>
                    <button onClick={e=>{ e.stopPropagation(); onEdit && onEdit(p); onClose(); }}
                      style={{ flex:1, padding:'4px', background:'#2563eb', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:9, fontWeight:'bold' }}>
                      ✎ Edit
                    </button>
                    <button onClick={e=>{ e.stopPropagation(); deletePattern(p); }}
                      disabled={deleting===p.id}
                      style={{ flex:1, padding:'4px', background:'#991b1b', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:9 }}>
                      {deleting===p.id ? '…' : '✕ Delete'}
                    </button>
                  </div>
                </div>
                {/* Expanded measurements when selected */}
                {selIdx === i && Object.keys(p.measurements||{}).length > 0 && (
                  <div style={{ padding:'8px 10px', borderTop:'1px solid #334155', background:'#0f172a' }}>
                    <div style={{ fontSize:9, fontWeight:'bold', color:'#64748b', marginBottom:4 }}>MEASUREMENTS</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'2px 8px' }}>
                      {Object.entries(p.measurements).map(([k,v]) => (
                        <div key={k} style={{ fontSize:9, color:'#94a3b8', display:'flex', justifyContent:'space-between' }}>
                          <span style={{ color:'#64748b' }}>{k}</span>
                          <span style={{ color:'#34d399', fontWeight:'bold' }}>{v} cm</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function MarkerMaker({ db, onClose, preloadStyle }) {
  const [step, setStep] = useState(preloadStyle ? 2 : 1);  // jump to step 2 if style pre-loaded
  const [selectedStyle, setSelectedStyle] = useState(preloadStyle || null);
  const [showGallery, setShowGallery] = useState(false);
  const [patterns, setPatterns] = useState([]);
  const [specData, setSpecData] = useState(null);
  const [gradingRules, setGradingRules] = useState({});
  const [saving, setSaving] = useState(false);

  // Save all patterns (with any adjustments) back to style in DB
  const savePatterns = async () => {
    if (!selectedStyle) return;
    setSaving(true);
    const updated = { ...selectedStyle, patterns, updatedOn: new Date().toISOString() };
    try {
      await fetch(SAVE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'style', data: updated }),
      });
      alert(`✓ ${patterns.length} patterns saved to style "${selectedStyle.name}"`);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const canGoNext = () => {
    if (step === 1 && !selectedStyle) return false;
    if (step === 2 && patterns.length === 0) return false;
    return true;
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ background: '#1e293b', color: 'white', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Scissors size={18} color="#38bdf8" />
          <span style={{ fontWeight: 'bold', fontSize: '1rem' }}>MARKER MAKER</span>
          {selectedStyle && (
            <span style={{ fontSize: 11, color: '#94a3b8', borderLeft: '1px solid #334155', paddingLeft: 10 }}>
              {selectedStyle.name || selectedStyle.styleCode}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selectedStyle && patterns.length > 0 && (
            <button onClick={savePatterns} disabled={saving}
              style={{ padding: '5px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10, fontWeight: 'bold' }}>
              {saving ? '⟳' : '💾'} Save Patterns
            </button>
          )}
          <button onClick={() => setShowGallery(true)}
            style={{ padding:'5px 12px', background:'#334155', color:'#38bdf8', border:'1px solid #475569', borderRadius:5, cursor:'pointer', fontSize:10, fontWeight:'bold' }}>
            📐 Pattern Library
          </button>
          <button onClick={onClose}
            style={{ padding: '5px 10px', background: '#334155', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 10 }}>
            ✕ Close
          </button>
        </div>
      </div>

      {/* Pattern Library Gallery */}
      {showGallery && <PatternGallery db={db} onClose={() => setShowGallery(false)} onEdit={(p) => { setPatterns([p]); setStep(5); }} />}
      {/* Wizard steps */}
      <WizardHeader step={step} onStepClick={s => {
        if (s <= step || (s === step + 1 && canGoNext())) setStep(s);
      }} />

      {/* Step content */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {step === 1 && (
          <StyleSelector db={db} onSelect={s => { setSelectedStyle(s); setPatterns([]); setStep(2); }} />
        )}
        {step === 2 && (
          <PatternLoader style={selectedStyle} db={db} patterns={patterns} setPatterns={setPatterns}
            gradingRules={gradingRules} setGradingRules={setGradingRules} />
        )}
        {step === 3 && (
          <SpecSheetImporter
            style={selectedStyle}
            specData={specData}
            setSpecData={setSpecData}
            onImportComplete={d => { setSpecData(d); setStep(4); }}
          />
        )}
        {step === 4 && (
          <SpecCrossCheck
            patterns={patterns}
            specData={specData}
            onAdjust={adj => { setPatterns(adj); setStep(5); }}
          />
        )}
        {step === 5 && (
          <PatternEditor patterns={patterns} setPatterns={setPatterns} />
        )}
        {step === 6 && (
          <GradingView
            patterns={patterns}
            specData={specData}
            gradingRules={gradingRules}
            setGradingRules={setGradingRules}
          />
        )}
        {step === 7 && (
          <SeamChecker
            patterns={patterns}
            gradingRules={gradingRules}
            activeSizes={Object.keys(specData?.sizes || { M: 1 })}
          />
        )}
        {step === 8 && (
          <MarkerPlanner
            patterns={patterns}
            specData={specData}
            gradingRules={gradingRules}
          />
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ background: 'white', borderTop: '1px solid #e2e8f0', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <button
          onClick={() => setStep(s => Math.max(1, s - 1))}
          disabled={step === 1}
          style={{ padding: '7px 16px', background: step === 1 ? '#f1f5f9' : '#e2e8f0', border: 'none', borderRadius: 6,
            cursor: step === 1 ? 'not-allowed' : 'pointer', fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>Step {step} of {STEPS.length}</span>
        <button
          onClick={() => setStep(s => Math.min(STEPS.length, s + 1))}
          disabled={step === STEPS.length || !canGoNext()}
          style={{ padding: '7px 16px',
            background: (step === STEPS.length || !canGoNext()) ? '#f1f5f9' : '#0ea5e9',
            color: (step === STEPS.length || !canGoNext()) ? '#94a3b8' : 'white',
            border: 'none', borderRadius: 6,
            cursor: (step === STEPS.length || !canGoNext()) ? 'not-allowed' : 'pointer',
            fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
